import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { showToast } from "../utils/customToast";
import DeleteConfirmModal from "../Component/DeleteModal/DeleteConfirmModal";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./Dutyslip.css";
import { apiWithLog } from "../utils/apiWithLog";
import { SECURITY_KEY } from "../globals";
import { downloadDutySlip } from "../utils/dashboardPdfHelper";
import { TableRowsSkeleton } from "../Component/Skeleton";
import SearchableSelect from "../Component/SearchableSelect/SearchableSelect";
import { cleanPartyOrAddress } from "../utils/stringUtils";

const Dutyslip = () => {
  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL;
  const BASE_REST_API_URL = process.env.REACT_APP_BASE_REST_API_URL;
  const today = new Date().toISOString().split("T")[0];

  const buildLogoUrl = (fileName) => {
    if (!fileName || fileName === "null") return null;
    const path = fileName.toString().trim();
    if (/^https?:\/\//i.test(path)) return path;
    let cleaned = path
      .replace(/^~[\\/]+/, "")
      .replace(/\\/g, "/")
      .replace(/^\/+/, "");
    const restRoot = (BASE_REST_API_URL || "").replace(/\/+$/, "");
    if (/^uploads\//i.test(cleaned)) {
      return `${restRoot.replace(/\/restApi$/i, "")}/${cleaned}`;
    }
    return `${restRoot}/${cleaned}`;
  };

  const fetchRestCompany = async (companyId) => {
    if (!companyId || !BASE_REST_API_URL) return {};

    const data = new URLSearchParams();
    data.append("SecurityKey", SECURITY_KEY);
    data.append("TableName", "company");
    data.append("WhereCondition", `CompID=${companyId}`);
    data.append("*", "*");

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(`${BASE_REST_API_URL}/SelectData.aspx`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
        signal: controller.signal,
      });
      const json = await response.json();
      return json.Response?.[0] || {};
    } finally {
      clearTimeout(timeout);
    }
  };

  const getCurrentMonthFirstDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}-01`;
  };

  const getCurrentMonthLastDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate();
    const monthStr = String(month).padStart(2, "0");
    const dayStr = String(lastDay).padStart(2, "0");
    return `${year}-${monthStr}-${dayStr}`;
  };

  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(today);
  const [filterFromDate, setFilterFromDate] = useState(getCurrentMonthFirstDate());
  const [filterToDate, setFilterToDate] = useState(today);
  const [filterPartyName, setFilterPartyName] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const lastCompanyId = useRef(null);

  const [dutyslipList, setDutyslipList] = useState([]);
  const [isLoadingDutyslip, setIsLoadingDutyslip] = useState(false);
  const [uninvoicedSlipIds, setUninvoicedSlipIds] = useState(new Set());
  const [uninvoicedLoaded, setUninvoicedLoaded] = useState(false);

  const [parties, setParties] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const [editId, setEditId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEndManual, setIsEndManual] = useState(false);

  const [showDutySlipModal, setShowDutySlipModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewCompany, setPreviewCompany] = useState(null);

  const [kmError, setKmError] = useState("");

  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [vehicleTypesList, setVehicleTypesList] = useState([]);
  const [ownersList, setOwnersList] = useState([]);
  const [isAddingVehicle, setIsAddingVehicle] = useState(false);
  const [newVehicleForm, setNewVehicleForm] = useState({
    vtypeName: "",
    vehicleNumber: "",
    ownerID: "",
    modelYear: new Date().getFullYear(),
  });

  const getToday = () => {
    const now = new Date();
    const ist = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
    );
    const year = ist.getFullYear();
    const month = String(ist.getMonth() + 1).padStart(2, "0");
    const day = String(ist.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getSlipDateTimeIST = (dateOnly) => {
    const now = new Date();
    const ist = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
    );
    const hours = String(ist.getHours()).padStart(2, "0");
    const minutes = String(ist.getMinutes()).padStart(2, "0");
    const seconds = String(ist.getSeconds()).padStart(2, "0");
    return `${dateOnly} ${hours}:${minutes}:${seconds}`;
  };

  const [openTimeParts, setOpenTimeParts] = useState({
    hh: "12",
    mm: "00",
    ap: "AM",
  });
  const [closeTimeParts, setCloseTimeParts] = useState({
    hh: "12",
    mm: "00",
    ap: "AM",
  });
  const [timeParts, setTimeParts] = useState({ hh: "12", mm: "00", ap: "AM" });
  const [endTimeParts, setEndTimeParts] = useState({
    hh: "08",
    mm: "00",
    ap: "AM",
  });

  const buildDateTime = (date, parts) => `${date} ${buildTime24(parts)}`;

  const buildTime24FromParts = (parts) => {
    let h = parseInt(parts.hh, 10);
    if (parts.ap === "AM") {
      if (h === 12) h = 0;
    } else {
      if (h !== 12) h += 12;
    }
    return `${String(h).padStart(2, "0")}:${parts.mm}:00`;
  };

  const [dutyslip, setDutyslip] = useState({
    slipDate: getToday(),
    openDate: getToday(),
    closeDate: getToday(),
    totalTime: "",
    partyName: "",
    address: "",
    clientAddress: "",
    clientMobile: "",
    clientName: "",
    vehicleType: "",
    vehicleNo: "",
    acType: "",
    driverName: "",
    referedBy: "",
    kmStart: "",
    kmEnd: "",
    totalKm: "",
    bookingId: null,
  });

  const [dateWiseRows, setDateWiseRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState("");

  const getCompanyId = () => {
    try {
      const company = JSON.parse(localStorage.getItem("selectedCompany"));
      return company?.id || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const id = getCompanyId();
      if (id !== lastCompanyId.current) {
        lastCompanyId.current = id;
        setSelectedCompanyId(id);
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const refreshVehicles = async (compId) => {
    try {
      const res = await axios.get(`${BASE_API_URL}/invoice/vehicles/${compId}`);
      if (res.data?.success) setVehicles(res.data.data || []);
    } catch (e) {
      console.warn("refreshVehicles error:", e);
    }
  };

  const fetchVehicleDropdowns = async (compId) => {
    try {
      if (compId) {
        const res = await axios.get(`${BASE_API_URL}/vehicles/dropdowns/${compId}`);
        if (res.data?.success) {
          if (res.data.vehicleTypes?.length) setVehicleTypesList(res.data.vehicleTypes);
          setOwnersList(res.data.owners || []);
        }
      }
      const vtRes = await axios.get(`${BASE_API_URL}/vehicles/vehicletypes`);
      if (vtRes.data?.success && vtRes.data.vehicleTypes?.length) {
        setVehicleTypesList(vtRes.data.vehicleTypes);
      }
    } catch (e) {
      console.warn("fetchVehicleDropdowns error:", e);
    }
  };

  useEffect(() => {
    fetchVehicleDropdowns(selectedCompanyId);
  }, []);

  useEffect(() => {
    if (!selectedCompanyId) return;
    axios
      .get(`${BASE_API_URL}/invoice/dropdowns/${selectedCompanyId}`)
      .then((res) => {
        if (res.data?.success) {
          const list = (res.data.data || []).map((p) => ({
            ...p,
            HEADName: cleanPartyOrAddress(p.HEADName),
            ADDRESS1: cleanPartyOrAddress(p.ADDRESS1),
          }));
          setParties(list);
        }
      });
    axios
      .get(`${BASE_API_URL}/invoice/vehicles/${selectedCompanyId}`)
      .then((res) => {
        if (res.data?.success) setVehicles(res.data.data || []);
      });
    fetchVehicleDropdowns(selectedCompanyId);
  }, [selectedCompanyId]);

  const allVehicleTypes = useMemo(() => {
    const map = new Map();
    vehicleTypesList.forEach((vt) => {
      if (vt.VehicleTypeName) {
        map.set(vt.VehicleTypeName.toLowerCase(), vt.VehicleTypeName);
      }
    });
    vehicles.forEach((v) => {
      if (v.VehicleTypeName && !map.has(v.VehicleTypeName.toLowerCase())) {
        map.set(v.VehicleTypeName.toLowerCase(), v.VehicleTypeName);
      }
    });
    return Array.from(map.values()).sort();
  }, [vehicleTypesList, vehicles]);

  const openAddVehicleModalForDutySlip = (currentVehicleType) => {
    setNewVehicleForm({
      vtypeName: currentVehicleType || "",
      vehicleNumber: "",
      self: 0,
      ownerID: "",
      modelYear: new Date().getFullYear(),
    });
    if (selectedCompanyId) fetchVehicleDropdowns(selectedCompanyId);
    setShowAddVehicleModal(true);
  };

  const handleAddVehicleSubmit = async () => {
    const { vtypeName, vehicleNumber, ownerID, modelYear, self } = newVehicleForm;

    if (!vtypeName.trim()) { showToast.warn("Vehicle Type name is required"); return; }
    if (!vehicleNumber.trim()) { showToast.warn("Vehicle Number is required"); return; }
    if (!ownerID) { showToast.warn("Owner is required"); return; }
    if (!selectedCompanyId) { showToast.warn("Select a company first"); return; }

    setIsAddingVehicle(true);
    try {
      let savedTypeName = vtypeName.trim();
      if (newVehicleForm.isCustomType) {
        try {
          const vtRes = await axios.post(`${BASE_API_URL}/vehicles/vehicletypes/create`, {
            VTYPE: vtypeName.trim(),
          });

          if (vtRes.data?.success && vtRes.data.vehicleType?.VehicleTypeName) {
            savedTypeName = vtRes.data.vehicleType.VehicleTypeName;
          }
        } catch (vtErr) {
          console.warn("Vehicle type creation notice:", vtErr);
        }
      }

      const vRes = await axios.post(`${BASE_API_URL}/vehicles/create`, {
        vehicleNumber: vehicleNumber.trim(),
        vehicleType: savedTypeName,
        ownerID: ownerID,
        modelYear: modelYear ? parseInt(modelYear) : null,
        self: (self === 1 || self === "1") ? 1 : 0,
        CompID: selectedCompanyId,
      });

      if (!vRes.data?.success) {
        showToast.error(vRes.data?.error || "Failed to create vehicle");
        return;
      }

      showToast.save(`Vehicle "${vehicleNumber.trim()}" added successfully!`);

      await refreshVehicles(selectedCompanyId);
      await fetchVehicleDropdowns(selectedCompanyId);

      setDutyslip((prev) => ({
        ...prev,
        vehicleType: savedTypeName,
        vehicleNo: vehicleNumber.trim(),
      }));

      setShowAddVehicleModal(false);
    } catch (err) {
      showToast.error(err?.response?.data?.error || err?.response?.data?.message || "Server error");
    } finally {
      setIsAddingVehicle(false);
    }
  };

  useEffect(() => {
    if (!dutyslip.vehicleType || !selectedCompanyId) {
      setPackages([]);
      return;
    }

    axios
      .get(
        `${BASE_API_URL}/dutyslip/fares/list/${encodeURIComponent(dutyslip.vehicleType)}?CompID=${selectedCompanyId}`
      )
      .then((res) => {
        if (res.data?.success) {
          const list = res.data.data || [];
          setPackages(list);
          // Only auto-select if vehicle type has linked packages
          const firstAssigned = list.find((p) => p.IsAssigned === 1 && p.ID != null);
          if (firstAssigned) {
            setSelectedPackageId((prev) => prev || String(firstAssigned.ID));
            setDateWiseRows((prevRows) => {
              const normalized = normalizeDateWiseRows(prevRows);
              return normalized.map((row) => {
                if (!row.fareId) {
                  return {
                    ...row,
                    fareId: String(firstAssigned.ID),
                    packageName: firstAssigned.PackageName || "",
                  };
                }
                return row;
              });
            });
          }
        } else {
          setPackages([]);
        }
      })
      .catch(() => setPackages([]));
  }, [dutyslip.vehicleType, selectedCompanyId]);

  const buildTime24 = (parts) => {
    let h = parseInt(parts.hh);
    if (parts.ap === "AM") {
      if (h === 12) h = 0;
    } else {
      if (h !== 12) h += 12;
    }
    return `${String(h).padStart(2, "0")}:${parts.mm}:00`;
  };

  const TIME_HOURS = Array.from({ length: 12 }, (_, i) =>
    String(i + 1).padStart(2, "0"),
  );
  const TIME_MINUTES = Array.from({ length: 12 }, (_, i) =>
    String(i * 5).padStart(2, "0"),
  );

  const parseTimeStringToParts = (str) => {
    if (!str) return { hh: "12", mm: "00", ap: "AM" };
    const s = String(str).trim();
    if (!s || s === "-") return { hh: "12", mm: "00", ap: "AM" };

    let cleanTime = s;
    if (cleanTime.includes("T")) {
      cleanTime = cleanTime.split("T")[1];
    } else if (cleanTime.includes(" ") && (cleanTime.includes("-") || cleanTime.includes("/"))) {
      const parts = cleanTime.split(/\s+/);
      cleanTime = parts.slice(1).join(" ");
    }

    const match = cleanTime.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?/i);
    if (!match) return { hh: "12", mm: "00", ap: "AM" };

    let h = parseInt(match[1], 10);
    let mm = match[2];
    let ap = match[3] ? match[3].toUpperCase() : null;

    if (!ap) {
      if (h >= 12) {
        ap = "PM";
        if (h > 12) h -= 12;
      } else {
        ap = "AM";
        if (h === 0) h = 12;
      }
    } else {
      if (h === 0) h = 12;
      else if (h > 12) {
        ap = "PM";
        h -= 12;
      }
    }

    let hh = String(h).padStart(2, "0");
    if (!TIME_HOURS.includes(hh)) hh = "12";
    if (!TIME_MINUTES.includes(mm)) {
      const nearest = TIME_MINUTES.reduce((prev, curr) =>
        Math.abs(Number(curr) - Number(mm)) < Math.abs(Number(prev) - Number(mm))
          ? curr
          : prev,
      );
      mm = nearest;
    }

    return { hh, mm, ap: ap || "AM" };
  };

  const formatTimePartsToString = (parts) =>
    `${parts.hh}:${parts.mm} ${parts.ap}`;

  const timePartsToMinutes = (parts) => {
    let h = parseInt(parts.hh, 10) % 12;
    if (parts.ap === "PM") h += 12;
    return h * 60 + parseInt(parts.mm, 10);
  };

  const overnightAwareDiffMinutes = (openMin, closeMin) => {
    if (openMin === closeMin) return 0;
    return closeMin > openMin ? closeMin - openMin : closeMin + 1440 - openMin;
  };

  const hasInputValue = (value) =>
    value !== null && value !== undefined && String(value).trim() !== "";

  const firstFilledValue = (...values) =>
    values.find((value) => hasInputValue(value)) ?? "";

  // Parse "Xh Ym" into total minutes
  const parseTotalTimeToMinutes = (str) => {
    if (!str || typeof str !== "string") return 0;
    let hours = 0,
      minutes = 0;
    const hMatch = str.match(/(\d+)\s*h/i);
    const mMatch = str.match(/(\d+)\s*m/i);
    if (hMatch) hours = parseInt(hMatch[1], 10);
    if (mMatch) minutes = parseInt(mMatch[1], 10);
    if (!hMatch && !mMatch) {
      const num = parseInt(str, 10);
      if (!isNaN(num)) hours = num;
    }
    return hours * 60 + minutes;
  };

  const formatMinutesToTotalTime = (minutes) => {
    if (minutes === null || minutes === undefined || isNaN(minutes) || minutes < 0) return "";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (m > 0) return `${h}h ${m}m`;
    return `${h}h`;
  };

  const toInputDate = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value.split("T")[0].split(" ")[0];
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (value) => {
    if (!value || value === "null" || value === "undefined" || value === "-") return "";
    const str = String(value).trim();
    if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(str)) {
      const parts = str.split(/[-/]/);
      return `${String(parts[0]).padStart(2, "0")}-${String(parts[1]).padStart(2, "0")}-${parts[2]}`;
    }
    if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(str)) {
      const parts = str.split("T")[0].split(" ")[0].split(/[-/]/);
      return `${String(parts[2]).padStart(2, "0")}-${String(parts[1]).padStart(2, "0")}-${parts[0]}`;
    }
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    }
    return str;
  };

  const getNextDate = (currentDate) => {
    if (!currentDate) return toInputDate(getToday());
    const d = new Date(`${currentDate}T00:00:00`);
    if (isNaN(d.getTime())) return toInputDate(getToday());
    d.setDate(d.getDate() + 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const createBlankDateWiseRow = (date) => ({
    date,
    kmStartGarage: "",
    kmStartUser: "",
    kmClose: "",
    kmCloseGarage: "",
    totalKm: "",
    openingTime: "",
    closingTime: "",
    totalTime: "",
    timeError: "",
    tripDetails: "",
    fareId: selectedPackageId || "",
    packageName: packages.find((p) => String(p.ID) === String(selectedPackageId))?.PackageName || "",
  });

  const getDateRange = (startDate, endDate) => {
    const start = toInputDate(startDate);
    const end = toInputDate(endDate || startDate);
    if (!start) return [];
    const startObj = new Date(`${start}T00:00:00`);
    const endObj = new Date(`${end || start}T00:00:00`);
    if (Number.isNaN(startObj.getTime()) || Number.isNaN(endObj.getTime())) {
      return [start];
    }
    const dates = [];
    const cursor = new Date(startObj);
    const last = endObj >= startObj ? endObj : startObj;
    while (cursor <= last) {
      const year = cursor.getFullYear();
      const month = String(cursor.getMonth() + 1).padStart(2, "0");
      const day = String(cursor.getDate()).padStart(2, "0");
      dates.push(`${year}-${month}-${day}`);
      cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
  };

  const normalizeDateWiseRows = (rows = []) =>
    rows.map((row) => ({
      ...createBlankDateWiseRow(
        toInputDate(row.date || row.tripDate || row.TripDate),
      ),
      kmStartGarage: row.kmStartGarage ?? row.KMStartGarage ?? "",
      kmStartUser: row.kmStartUser ?? row.KMStartUser ?? "",
      kmClose: row.kmClose ?? row.KMClose ?? "",
      kmCloseGarage: row.kmCloseGarage ?? row.KMCloseGarage ?? "",
      totalKm: row.totalKm ?? row.TotalKM ?? "",
      openingTime: row.openingTime || row.OpeningTime || "",
      closingTime: row.closingTime || row.ClosingTime || "",
      totalTime: row.totalTime || row.TotalTime || "",
      timeError: row.timeError || "",
      tripDetails: row.tripDetails ?? row.TripDetails ?? "",
      fareId: row.fareId ?? row.FareId ?? "",
      packageName: row.packageName ?? row.PackageName ?? "",
    }));

  // ===== getRowsSummary computes both totalKm and totalTime =====
  const getRowsSummary = (rows) => {
    const cleanRows = normalizeDateWiseRows(rows).filter((row) => row.date);
    const first = cleanRows[0] || {};
    const lastWithClose =
      [...cleanRows]
        .reverse()
        .find(
          (row) =>
            hasInputValue(row.kmCloseGarage) || hasInputValue(row.kmClose),
        ) || {};
    const totalFromRows = cleanRows.reduce(
      (sum, row) => sum + (Number(row.totalKm) || 0),
      0,
    );
    const kmStart = firstFilledValue(first.kmStartUser, first.kmStartGarage);
    const kmEnd = firstFilledValue(
      lastWithClose.kmClose,
      lastWithClose.kmCloseGarage,
    );
    const calculatedTotal =
      hasInputValue(kmStart) &&
      hasInputValue(kmEnd) &&
      Number(kmEnd) >= Number(kmStart)
        ? Number(kmEnd) - Number(kmStart)
        : "";
    const hasRowTotals = cleanRows.some((row) => hasInputValue(row.totalKm));
    const finalTotalKm = hasRowTotals
      ? totalFromRows
      : hasInputValue(calculatedTotal)
        ? calculatedTotal
        : "";

    // Compute total time from all rows
    let totalMinutes = 0;
    cleanRows.forEach((row) => {
      if (row.totalTime) {
        totalMinutes += parseTotalTimeToMinutes(row.totalTime);
      }
    });
    const totalTime = formatMinutesToTotalTime(totalMinutes);

    return {
      kmStart,
      kmEnd,
      totalKm: finalTotalKm,
      totalTime,
      totalTimeMinutes: totalMinutes,
    };
  };

  // ===== mergeDateWiseRows – recalculates row totals and updates grand totals =====
  const mergeDateWiseRows = (startDate, endDate, rows = [], summary = {}) => {
    const rangeDates = getDateRange(startDate, endDate);
    const existingRows = normalizeDateWiseRows(rows);
    const existingMap = {};
    existingRows.forEach((row) => {
      if (row.date && (rangeDates.length === 0 || rangeDates.includes(row.date))) {
        existingMap[row.date] = row;
      }
    });

    if (rangeDates.length) {
      rangeDates.forEach((date) => {
        if (!existingMap[date]) {
          existingMap[date] = createBlankDateWiseRow(date);
        }
      });
    }

    let merged = Object.values(existingMap);
    if (rangeDates.length) {
      merged = merged.filter((r) => rangeDates.includes(r.date));
    }
    merged.sort((a, b) => a.date.localeCompare(b.date));

    const openTimeStr = summary.openingTime || (openTimeParts ? formatTimePartsToString(openTimeParts) : "");
    const closeTimeStr = summary.closingTime || (closeTimeParts ? formatTimePartsToString(closeTimeParts) : "");

    if (merged.length) {
      merged[0] = {
        ...merged[0],
        kmStartGarage: firstFilledValue(
          merged[0].kmStartGarage,
          summary.kmStart,
        ),
        kmStartUser: firstFilledValue(merged[0].kmStartUser, summary.kmStart),
        openingTime: summary.openingTime || openTimeStr || merged[0].openingTime || "",
      };
      const lastIndex = merged.length - 1;
      merged[lastIndex] = {
        ...merged[lastIndex],
        kmClose: firstFilledValue(merged[lastIndex].kmClose, summary.kmEnd),
        kmCloseGarage: firstFilledValue(
          merged[lastIndex].kmCloseGarage,
          summary.kmEnd,
        ),
        closingTime: summary.closingTime || closeTimeStr || merged[lastIndex].closingTime || "",
      };
    }

    // Recalculate each row's total time and total KM
    merged.forEach((r) => {
      // Total KM
      const kmStartVal = firstFilledValue(r.kmStartGarage, r.kmStartUser);
      const kmCloseVal = firstFilledValue(r.kmCloseGarage, r.kmClose);
      if (hasInputValue(kmStartVal) && hasInputValue(kmCloseVal)) {
        const kmStart = Number(kmStartVal);
        const kmClose = Number(kmCloseVal);
        if (!Number.isNaN(kmStart) && !Number.isNaN(kmClose) && kmClose >= kmStart) {
          r.totalKm = kmClose - kmStart;
        } else {
          r.totalKm = "";
        }
      } else {
        r.totalKm = "";
      }
      // Total Time
      if (r.openingTime && r.closingTime) {
        const openMin = timePartsToMinutes(
          parseTimeStringToParts(r.openingTime),
        );
        const closeMin = timePartsToMinutes(
          parseTimeStringToParts(r.closingTime),
        );
        const diff = overnightAwareDiffMinutes(openMin, closeMin);
        r.totalTime = formatMinutesToTotalTime(diff);
        r.timeError = "";
      } else {
        r.totalTime = "";
      }
    });

    // Update grand totals in dutyslip state
    const grandSummary = getRowsSummary(merged);
    setDutyslip((prev) => ({
      ...prev,
      kmStart: grandSummary.kmStart,
      kmEnd: grandSummary.kmEnd,
      totalKm: grandSummary.totalKm,
      totalTime: grandSummary.totalTime,
    }));

    return merged;
  };

  const handleReportingDateChange = (newDate) => {
    if (!newDate) return;
    setDateWiseRows((prevRows) => {
      const rows = normalizeDateWiseRows(prevRows);
      if (!rows.length) {
        const initialRow = {
          ...createBlankDateWiseRow(newDate),
          openingTime: formatTimePartsToString(openTimeParts),
          closingTime: formatTimePartsToString(closeTimeParts),
        };
        const summary = getRowsSummary([initialRow]);
        setDutyslip((p) => ({
          ...p,
          openDate: newDate,
          closeDate: newDate,
          kmStart: summary.kmStart,
          kmEnd: summary.kmEnd,
          totalKm: summary.totalKm,
          totalTime: summary.totalTime,
        }));
        return [initialRow];
      }

      let curr = newDate;
      const updatedRows = rows.map((r) => {
        const rowDate = curr;
        curr = getNextDate(curr);
        return {
          ...r,
          date: rowDate,
        };
      });

      const lastDate = updatedRows[updatedRows.length - 1].date;
      const summary = getRowsSummary(updatedRows);
      setDutyslip((p) => ({
        ...p,
        openDate: newDate,
        closeDate: lastDate,
        kmStart: summary.kmStart,
        kmEnd: summary.kmEnd,
        totalKm: summary.totalKm,
        totalTime: summary.totalTime,
      }));
      return updatedRows;
    });
  };

  const updateBoundaryDateWiseTime = (boundary, timeText) => {
    setDateWiseRows((prevRows) => {
      const rows = normalizeDateWiseRows(prevRows);
      if (!rows.length) return rows;
      const index = boundary === "open" ? 0 : rows.length - 1;
      const field = boundary === "open" ? "openingTime" : "closingTime";
      rows[index] = { ...rows[index], [field]: timeText };
      const merged = mergeDateWiseRows(
        dutyslip.openDate,
        dutyslip.closeDate,
        rows,
      );
      return merged;
    });
  };

  // ---- Removed old useEffect that computed totalTime from open/close ----

  const parseDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return new Date(NaN);
    let year, month, day;
    const cleanDate = dateStr.trim().split(" ")[0].split("T")[0];
    if (cleanDate.includes("-")) {
      const parts = cleanDate.split("-");
      if (parts[0].length === 4) {
        [year, month, day] = parts;
      } else {
        [day, month, year] = parts;
      }
    } else if (cleanDate.includes("/")) {
      const parts = cleanDate.split("/");
      if (parts[0].length === 4) {
        [year, month, day] = parts;
      } else {
        [day, month, year] = parts;
      }
    } else {
      return new Date(cleanDate);
    }
    const [h, m, s] = timeStr.split(":");
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(h || 0),
      Number(m || 0),
      Number(s || 0),
    );
  };

  // Not used for grand total, kept for potential other uses
  const calculateTotalTime = (openDate, openParts, closeDate, closeParts) => {};

  const updateOpenTime = (key, value) => {
    const v = key === "ap" ? value : String(value).padStart(2, "0");
    setOpenTimeParts((prev) => {
      const updated = { ...prev, [key]: v };
      const timeStr = formatTimePartsToString(updated);
      setDutyslip((p) => ({ ...p, openTime: timeStr }));

      // Directly update first row's openingTime in Date Wise Trip Details
      setDateWiseRows((prevRows) => {
        const rows = normalizeDateWiseRows(prevRows);
        if (!rows.length) return rows;
        const first = { ...rows[0], openingTime: timeStr };
        if (first.openingTime && first.closingTime) {
          const openMin = timePartsToMinutes(parseTimeStringToParts(first.openingTime));
          const closeMin = timePartsToMinutes(parseTimeStringToParts(first.closingTime));
          const diff = overnightAwareDiffMinutes(openMin, closeMin);
          first.totalTime = formatMinutesToTotalTime(diff);
        }
        const nextRows = [first, ...rows.slice(1)];
        const summary = getRowsSummary(nextRows);
        setDutyslip((p) => ({
          ...p,
          kmStart: summary.kmStart,
          kmEnd: summary.kmEnd,
          totalKm: summary.totalKm,
          totalTime: summary.totalTime,
        }));
        return nextRows;
      });

      return updated;
    });
  };

  const updateCloseTime = (key, value) => {
    const v = key === "ap" ? value : String(value).padStart(2, "0");
    setCloseTimeParts((prev) => {
      const updated = { ...prev, [key]: v };
      const timeStr = formatTimePartsToString(updated);
      setDutyslip((p) => ({ ...p, closeTime: timeStr }));
      updateBoundaryDateWiseTime("close", timeStr);
      return updated;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDutyslip((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "address" || name === "clientAddress") {
        const cleaned = String(value).replace(/^['`\s\\]+/, "");
        updated.address = cleaned;
        updated.clientAddress = cleaned;
      }
      if (name === "partyName") {
        updated.partyName = String(value).replace(/^['`\s\\]+/, "");
      }
      if (name === "openDate") {
        autoCalculateEnd(value, updated.openTime);
        handleReportingDateChange(value);
        return updated;
      }
      if (name === "closeDate") {
        setIsEndManual(true);
      }
      if (name === "kmStart" || name === "kmEnd") {
        // kept for compatibility, but totals are derived from rows
        const start = parseFloat(updated.kmStart);
        const end = parseFloat(updated.kmEnd);
        if (updated.kmEnd === "" || updated.kmStart === "") {
          updated.totalKm = "";
          setErrors((prev) => ({ ...prev, km: "" }));
        } else if (end < start) {
          updated.totalKm = "";
          setErrors((prev) => ({
            ...prev,
            km: "KM Close cannot be less than KM Start",
          }));
        } else {
          updated.totalKm = end - start;
          setErrors((prev) => ({ ...prev, km: "" }));
        }
      }
      if (name === "closeDate") {
        setDateWiseRows((prevRows) =>
          mergeDateWiseRows(
            updated.openDate,
            value,
            prevRows,
            {
              kmStart: updated.kmStart,
              kmEnd: updated.kmEnd,
              totalKm: updated.totalKm,
              openingTime: openTimeParts
                ? formatTimePartsToString(openTimeParts)
                : "",
              closingTime: closeTimeParts
                ? formatTimePartsToString(closeTimeParts)
                : "",
            },
          ),
        );
      }
      if (name === "kmStart" || name === "kmEnd") {
        setDateWiseRows((prevRows) =>
          mergeDateWiseRows(updated.openDate, updated.closeDate, prevRows, {
            kmStart: updated.kmStart,
            kmEnd: updated.kmEnd,
            totalKm: updated.totalKm,
          }),
        );
      }
      return updated;
    });
  };

  const openDutySlipModal = (b) => {
    setSelectedBooking(b);
    const start = toInputDate(b.bookingDate || b.BookingDate || b.reportingDate || b.startDate || b.date || b.openDate) || selectedDate || getToday();
    const end = toInputDate(b.endBookingDate || b.BookingEndDate || b.endDate || b.closingDate || b.closeDate) || start;

    setDutyslip({
      slipDate: start,
      openDate: start,
      closeDate: end,
      partyName: cleanPartyOrAddress(b.partyName || b.PartyName || ""),
      address: cleanPartyOrAddress(b.address || b.partyAddress || b.PartyAddress || b.clientAddress || ""),
      clientName: b.guestName || b.clientName || "",
      clientAddress: cleanPartyOrAddress(b.clientAddress || b.address || ""),
      clientMobile: b.mobile || b.clientMobile || "",
      vehicleType: b.vehicleType || b.VehicleType || "",
      vehicleNo: b.vehicleNo || b.VehicleNo || "",
      acType: "AC",
      driverName: b.driverName || b.DriverName || "",
      kmStart: "",
      kmEnd: "",
      totalKm: "",
      totalTime: "",
      bookingId: b.id || null,
    });
    const initialRows = mergeDateWiseRows(start, end, [], {
      kmStart: "",
      kmEnd: "",
      totalKm: "",
    });
    setDateWiseRows(initialRows);
    setShowDutySlipModal(true);
  };

  const validateForm = () => {
    const missing = [];
    if (!dutyslip.partyName || !String(dutyslip.partyName).trim()) missing.push("Party Name");
    if (!dutyslip.clientName || !String(dutyslip.clientName).trim()) missing.push("Guest Name");
    if (!dutyslip.clientMobile || !String(dutyslip.clientMobile).trim()) missing.push("Guest Mobile");
    if (!dutyslip.vehicleType) missing.push("Vehicle Type");
    if (!dutyslip.driverName || !String(dutyslip.driverName).trim()) missing.push("Driver Name");

    const rows = normalizeDateWiseRows(dateWiseRows);
    const hasPackage = selectedPackageId || (rows && rows.some((r) => r.fareId || r.FareId));
    if (!hasPackage) {
      missing.push("Package");
    }

    if (missing.length > 0) {
      showToast.warn(`${missing.join(", ")} ${missing.length === 1 ? "is" : "are"} required`);
      return false;
    }

    if (!/^\d{10}$/.test(dutyslip.clientMobile)) {
      showToast.warn("Enter valid 10 digit mobile");
      return false;
    }

    const firstRow = rows[0] || {};
    if (
      !hasInputValue(firstRow.kmStartGarage) &&
      !hasInputValue(firstRow.kmStartUser)
    ) {
      showToast.warn("Starting KM is required in the first date row");
      return false;
    }

    const invalidDateRow = rows.some((row) => {
      const startVal = firstFilledValue(row.kmStartGarage, row.kmStartUser);
      const closeVal = firstFilledValue(row.kmCloseGarage, row.kmClose);
      if (hasInputValue(startVal) && hasInputValue(closeVal)) {
        return Number(closeVal) < Number(startVal);
      }
      return false;
    });
    if (invalidDateRow) {
      showToast.warn("Date-wise KM close cannot be less than KM start");
      return false;
    }

    return true;
  };

  const saveDutyslip = async (downloadAfterSave = false) => {
    if (isSaving) return;
    if (!validateForm()) return;
    try {
      setIsSaving(true);
      const slipDateTime = getSlipDateTimeIST(dutyslip.slipDate);
      const openTime = `${dutyslip.openDate} ${buildTime24FromParts(openTimeParts)}`;
      const closeTime = `${dutyslip.closeDate} ${buildTime24FromParts(closeTimeParts)}`;
      const dateWisePayload = buildDateWisePayload();
      const rowSummary = getRowsSummary(dateWiseRows);
      const totalTimeFloat = rowSummary.totalTimeMinutes / 60; // convert to hours

      const payload = {
        slipDate: slipDateTime,
        partyName: dutyslip.partyName,
        clientName: dutyslip.clientName,
        clientMobile: dutyslip.clientMobile,
        vehicleType: dutyslip.vehicleType,
        vehicleNo: dutyslip.vehicleNo || "",
        carType: dutyslip.acType || "AC",
        driverName: dutyslip.driverName,
        referedBy: (dutyslip.referedBy || "").substring(0, 50),
        fareId: selectedPackageId || null,
        bookingId: dutyslip.bookingId || null,
        clientAddress: dutyslip.address || dutyslip.clientAddress || "",
        kmStart: Number(rowSummary.kmStart) || 0,
        kmEnd: Number(rowSummary.kmEnd) || 0,
        totalKm: Number(rowSummary.totalKm) || 0,
        openTime,
        closeTime,
        totalTime: totalTimeFloat,
        compId: selectedCompanyId,
        dateWiseDetails: dateWisePayload,
      };
      const res = await apiWithLog({
        method: "POST",
        url: `${BASE_API_URL}/dutyslip/create`,
        data: payload,
        module: "DutySlip",
        action: "A",
      });
      if (res.data?.success) {
        showToast.save("Duty Slip saved successfully!");
        const newDutyslipId = res.data.id;
        if (downloadAfterSave) {
          await handleDownloadDutyslip(buildPdfRowFromForm(newDutyslipId));
        }
        setShowAddModal(false);
        setShowDutySlipModal(false);
        fetchDutyslip(selectedCompanyId, filterFromDate, filterToDate, filterPartyName);
        setDutyslip({
          slipDate: getToday(),
          openDate: getToday(),
          closeDate: getToday(),
          openTime: "00:00:00",
          closeTime: "00:00:00",
          totalTime: "",
          partyName: "",
          address: "",
          clientMobile: "",
          clientName: "",
          vehicleId: "",
          acType: "",
          driverName: "",
          referedBy: "",
          kmStart: "",
          kmEnd: "",
          totalKm: "",
        });
        setSelectedPackageId("");
      } else {
        showToast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      console.log(err);
      showToast.error("Server error");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!selectedCompanyId) return;
    axios
      .get(`${BASE_API_URL}/invoice/dropdowns/${selectedCompanyId}`)
      .then((res) => {
        if (res.data?.success) {
          const list = (res.data.data || []).map((p) => ({
            ...p,
            HEADName: cleanPartyOrAddress(p.HEADName),
            ADDRESS1: cleanPartyOrAddress(p.ADDRESS1),
          }));
          setParties(list);
        }
      });
    axios
      .get(`${BASE_API_URL}/invoice/vehicles/${selectedCompanyId}`)
      .then((res) => {
        if (res.data?.success) setVehicles(res.data.data || []);
      });
  }, [selectedCompanyId]);

  const autoCalculateEnd = (date, time) => {
    if (isEndManual) return;
    if (!date || !time) return;
    const [year, month, day] = date.split("-");
    const [hour, minute, second] = time.split(":");
    const start = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second || 0),
    );
    start.setHours(start.getHours() + 8);
    const endYear = start.getFullYear();
    const endMonth = String(start.getMonth() + 1).padStart(2, "0");
    const endDay = String(start.getDate()).padStart(2, "0");
    const endDate = `${endYear}-${endMonth}-${endDay}`;
    const hour24 = start.getHours();
    const minuteLocal = start.getMinutes();
    let hh12 = hour24 % 12;
    if (hh12 === 0) hh12 = 12;
    const ap = hour24 >= 12 ? "PM" : "AM";
    const hhStr = String(hh12).padStart(2, "0");
    const mmStr = String(minuteLocal).padStart(2, "0");
    setDutyslip((prev) => ({
      ...prev,
      endDutyslipDate: endDate,
      endTime: `${String(hour24).padStart(2, "0")}:${mmStr}:00`,
    }));
    setCloseTimeParts({ hh: hhStr, mm: mmStr, ap });
  };

  const updateTime = (key, value) => {
    const v = key === "ap" ? value : String(value).padStart(2, "0");
    setTimeParts((prev) => {
      const updated = { ...prev, [key]: v };
      const time24 = buildTime24(updated);
      setDutyslip((p) => {
        autoCalculateEnd(p.dutyslipDate, time24);
        return { ...p, time: time24 };
      });
      return updated;
    });
  };

  const updateEndTime = (key, value) => {
    setIsEndManual(true);
    const v = key === "ap" ? value : String(value).padStart(2, "0");
    setEndTimeParts((prev) => {
      const updated = { ...prev, [key]: v };
      const time24 = buildTime24(updated);
      setDutyslip((p) => ({ ...p, endTime: time24 }));
      return updated;
    });
  };

  const handlePartySelect = async (e) => {
    const rawVal = e?.target?.value !== undefined ? e.target.value : e;
    const name = cleanPartyOrAddress(rawVal);
    setDutyslip((p) => ({ ...p, partyName: name, address: "", mobile: "" }));
    if (!name || !selectedCompanyId) return;
    const res = await axios.get(
      `${BASE_API_URL}/invoice/dropdowns/details/${selectedCompanyId}?headName=${encodeURIComponent(
        name,
      )}`,
    );
    if (res.data?.success) {
      const p = res.data.data || {};
      const addr = cleanPartyOrAddress(p.ADDRESS1 || p.Address || "");
      setDutyslip((prev) => ({
        ...prev,
        address: addr,
        clientAddress: addr,
        mobile: (p.MOBILE || "").trim(),
      }));
    }
  };

  const fetchUninvoicedDutySlips = async (compId) => {
    if (!compId) return;
    try {
      const res = await axios.get(`${BASE_API_URL}/invoice/duty-slips/uninvoiced/${compId}`);
      if (res.data?.success && Array.isArray(res.data.data)) {
        const idSet = new Set(res.data.data.map((s) => Number(s.DutySlipID)));
        setUninvoicedSlipIds(idSet);
        setUninvoicedLoaded(true);
      }
    } catch (err) {
      console.warn("Could not fetch uninvoiced duty slips:", err);
    }
  };

  const fetchDutyslip = async (compId, fDate, tDate, party) => {
    if (!compId) return;
    try {
      setIsLoadingDutyslip(true);
      fetchUninvoicedDutySlips(compId);
      let url = `${BASE_API_URL}/dutyslip/dutyslip?compId=${compId}`;
      if (fDate && tDate) {
        url += `&fromDate=${fDate}&toDate=${tDate}`;
      } else if (fDate) {
        url += `&date=${fDate}`;
      } else if (tDate) {
        url += `&toDate=${tDate}`;
      }
      if (party) {
        url += `&partyName=${encodeURIComponent(party)}`;
      }
      const res = await axios.get(url);
      if (res.data?.success) {
        const records = (res.data.data || []).map((row) => ({
          id: row.DutyslipID || 0,
          dutyslipDate: row.DutyslipDate
            ? String(row.DutyslipDate).split("T")[0]
            : "",
          time: row.time24 || "00:00:00",
          time12: row.openTime12 || "",
          endDutyslipDate: row.DutyslipEndDate
            ? String(row.DutyslipEndDate).split("T")[0]
            : "",
          endTime: row.endTime24 || "00:00:00",
          endTime12: row.closeTime12 || "",
          partyName: cleanPartyOrAddress(row.PartyName || ""),
          partyAddress: cleanPartyOrAddress(row.ClientAddress || row.clientAddress || row.address || row.PartyAddress || ""),
          clientAddress: cleanPartyOrAddress(row.ClientAddress || row.clientAddress || row.address || row.PartyAddress || ""),
          ClientAddress: cleanPartyOrAddress(row.ClientAddress || row.clientAddress || row.address || row.PartyAddress || ""),
          address: cleanPartyOrAddress(row.ClientAddress || row.clientAddress || row.address || row.PartyAddress || ""),
          mobile: row.Mobile || "",
          vehicleType: row.VehicleType || row.CarType || "",
          vehicleNo: row.VehicleNo || row.vehicleNo || "",
          guestName: row.ClientName || "",
          driverName: row.DriverName || "",
          referedBy: row.ReferedBy || row.referedBy || "",
          fareId: row.FareId || row.fareId || null,
          packageName: row.PackageName || row.packageName || "",
          kmStart: row.KMStart || 0,
          kmEnd: row.KMClose || 0,
          totalKm: row.TotalKM || 0,
          totalTime: row.TotalTime || 0,
          invoiceId: row.InvoiceID || row.invoiceId || null,
          invoiceNo: row.InvoiceNo || row.invoiceNo || null,
          isInvoiceMade: Boolean(row.isInvoiceMade || row.InvoiceID || row.invoiceId || row.InvoiceNo || row.invoiceNo),
          dateWiseDetails: normalizeDateWiseRows(row.dateWiseDetails || []),
        }));
        setDutyslipList(records);
      } else {
        setDutyslipList([]);
      }
    } catch (error) {
      console.log("Fetch Dutyslip Error:", error);
      showToast.error("Failed to fetch duty slip");
      setDutyslipList([]);
    } finally {
      setIsLoadingDutyslip(false);
    }
  };

  const handleCreateInvoice = (slip) => {
    navigate(`/BillsInvoice?dutySlipId=${slip.id}&partyName=${encodeURIComponent(slip.partyName || "")}`);
  };

  const handleViewInvoice = (slip) => {
    if (slip.invoiceNo) {
      showToast.info(`Opening Invoice #${slip.invoiceNo}`);
      navigate(`/BillsInvoice?edit=${encodeURIComponent(slip.invoiceNo)}`);
    } else {
      showToast.info(`Invoice is already created for Duty Slip #${slip.id}`);
      navigate("/ViewInvoice");
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      fetchDutyslip(selectedCompanyId, filterFromDate, filterToDate, filterPartyName);
      fetchUninvoicedDutySlips(selectedCompanyId);
    }
  }, [selectedCompanyId, filterFromDate, filterToDate, filterPartyName]);

  const openEditDutyslip = (b) => {
    if (!b) return;
    setEditId(b.id);
    const openParts = parseTimeStringToParts(b.time12 || b.time || b.openTime12 || b.openTime);
    const closeParts = parseTimeStringToParts(b.endTime12 || b.endTime || b.closeTime12 || b.closeTime);
    setOpenTimeParts(openParts);
    setCloseTimeParts(closeParts);
    const formatTotalTime = (hoursFloat) => {
      if (!hoursFloat || isNaN(hoursFloat)) return "";
      const h = Math.floor(hoursFloat);
      const m = Math.round((hoursFloat - h) * 60);
      return `${h}h ${m}m`;
    };
    const openingTimeText = b.time12 || (b.time ? formatTimePartsToString(openParts) : "");
    const closingTimeText = b.endTime12 || (b.endTime ? formatTimePartsToString(closeParts) : "");
const savedRows = b.dateWiseDetails?.length
  ? normalizeDateWiseRows(b.dateWiseDetails)
  : [];
const rangeStart = savedRows.length ? toInputDate(savedRows[0].date) : (toInputDate(b.openDutyslipDate) || toInputDate(b.openDate) || toInputDate(b.OpeningDate) || toInputDate(b.dutyslipDate));
const rangeEnd = savedRows.length
  ? toInputDate(savedRows[savedRows.length - 1].date)
  : (toInputDate(b.endDutyslipDate) || toInputDate(b.closeDate) || toInputDate(b.ClosingDate) || rangeStart);
const editDateWiseRows = mergeDateWiseRows(
  rangeStart,
  rangeEnd,
  savedRows,
  {
    kmStart: b.kmStart ?? "",
    kmEnd: b.kmEnd ?? "",
    totalKm: b.totalKm ?? "",
    openingTime: openingTimeText,
    closingTime: closingTimeText,
  },
);
    // mergeDateWiseRows updates dutyslip totals, so we set state accordingly
    setDutyslip((prev) => ({
      ...prev,
      slipDate: toInputDate(b.dutyslipDate || b.DutyslipDate || b.slipDate) || rangeStart || "",
      openDate: rangeStart || "",
      closeDate: rangeEnd || "",
      totalTime: formatTotalTime(b.totalTime || b.TotalTime),
      partyName: cleanPartyOrAddress(b.partyName || b.PartyName || ""),
      address: cleanPartyOrAddress(b.ClientAddress || b.clientAddress || b.partyAddress || b.PartyAddress || b.address || b.Address || ""),
      clientAddress: cleanPartyOrAddress(b.ClientAddress || b.clientAddress || b.partyAddress || b.PartyAddress || b.address || b.Address || ""),
      clientName: b.guestName || b.clientName || b.ClientName || "",
      clientMobile: b.mobile || b.clientMobile || b.Mobile || "",
      vehicleType: b.vehicleType || b.VehicleType || "",
      vehicleNo: b.vehicleNo || b.VehicleNo || "",
      acType: "AC",
      driverName: b.driverName || b.DriverName || "",
      referedBy: b.referedBy || b.ReferedBy || "",
      kmStart: b.kmStart ?? b.KMStart ?? "",
      kmEnd: b.kmEnd ?? b.KMClose ?? "",
      totalKm: b.totalKm ?? b.TotalKM ?? "",
    }));
    setSelectedPackageId(b.fareId || b.FareId || "");
    setDateWiseRows(editDateWiseRows);
    setShowEditModal(true);
  };

  const updateDutyslip = async (downloadAfterSave = false) => {
    if (isSaving) return;
    if (!validateForm()) return;
    if (!editId) {
      showToast.error("Invalid Duty Slip ID");
      return;
    }
    try {
      setIsSaving(true);
      const slipDateTime = getSlipDateTimeIST(dutyslip.slipDate);
      const openTime = `${dutyslip.openDate} ${buildTime24FromParts(openTimeParts)}`;
      const closeTime = `${dutyslip.closeDate} ${buildTime24FromParts(closeTimeParts)}`;
      const dateWisePayload = buildDateWisePayload();
      const rowSummary = getRowsSummary(dateWiseRows);
      const totalTimeFloat = rowSummary.totalTimeMinutes / 60;

      const payload = {
        slipDate: slipDateTime,
        partyName: dutyslip.partyName,
        clientName: dutyslip.clientName,
        clientMobile: dutyslip.clientMobile,
        vehicleType: dutyslip.vehicleType,
        vehicleNo: dutyslip.vehicleNo || "",
        carType: dutyslip.acType || "AC",
        driverName: dutyslip.driverName,
        referedBy: (dutyslip.referedBy || "").substring(0, 50),
        fareId: selectedPackageId || null,
        bookingId: dutyslip.bookingId || null,
        clientAddress: dutyslip.address || dutyslip.clientAddress || "",
        kmStart: Number(rowSummary.kmStart) || 0,
        kmEnd: Number(rowSummary.kmEnd) || 0,
        totalKm: Number(rowSummary.totalKm) || 0,
        openTime,
        closeTime,
        totalTime: totalTimeFloat,
        compId: selectedCompanyId,
        dateWiseDetails: dateWisePayload,
      };
      const res = await apiWithLog({
        method: "PUT",
        url: `${BASE_API_URL}/dutyslip/update/${editId}`,
        data: payload,
        module: "DutySlip",
        action: "E",
      });
      if (res.data?.success) {
        showToast.save("Duty Slip updated successfully!");
        if (downloadAfterSave) {
          await handleDownloadDutyslip(buildPdfRowFromForm(editId));
        }
        setShowEditModal(false);
        fetchDutyslip(selectedCompanyId, filterFromDate, filterToDate, filterPartyName);
      } else {
        showToast.error(res.data?.message || "Update failed");
      }
    } catch (err) {
      console.log("Update Error:", err);
      showToast.error("Server error");
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteModal = (dutyslipItem) => {
    if (!dutyslipItem) return;
    const id = typeof dutyslipItem === "object" ? dutyslipItem.id : dutyslipItem;
    const name = typeof dutyslipItem === "object"
      ? (dutyslipItem.guestName ? `${dutyslipItem.partyName || "Duty Slip"} - ${dutyslipItem.guestName}` : `Duty Slip #${dutyslipItem.id}`)
      : `Duty Slip #${id}`;
    setDeleteId(id);
    setDeleteItemName(name);
    setShowDeleteModal(true);
  };

  const confirmDeleteDutyslip = async () => {
    if (isDeleting) return;
    if (!deleteId) return;
    try {
      setIsDeleting(true);
      await apiWithLog({
        method: "DELETE",
        url: `${BASE_API_URL}/dutyslip/delete/${deleteId}`,
        module: "DutySlip",
        action: "D",
      });
      showToast.delete("Duty Slip deleted successfully!");
      setShowDeleteModal(false);
      setDeleteId(null);
      setDeleteItemName("");
      fetchDutyslip(selectedCompanyId, filterFromDate, filterToDate, filterPartyName);
    } catch (err) {
      console.log(err);
      showToast.error("Delete failed");
    } finally {
      setIsDeleting(false);
    }
  };

  const format12Hour = (time24) => {
    if (!time24) return "-";
    const [h, m] = time24.split(":");
    let hour = parseInt(h, 10);
    const ap = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour}:${m} ${ap}`;
  };

  const val = (v) => (v === null || v === undefined || v === "" ? "-" : v);

  const buildDateWisePayload = (rows = dateWiseRows) =>
    normalizeDateWiseRows(rows).map((row) => ({
      date: row.date,
      kmStartGarage: Number(row.kmStartGarage) || 0,
      kmStartUser: Number(row.kmStartUser) || 0,
      kmClose: Number(row.kmClose) || 0,
      kmCloseGarage: Number(row.kmCloseGarage) || 0,
      totalKm: Number(row.totalKm) || 0,
      openingTime: row.openingTime || "",
      closingTime: row.closingTime || "",
      totalTime: row.totalTime || "",
      tripDetails: row.tripDetails || "",
      fareId: row.fareId || null,
    }));

  // ======= handleDateWiseChange with auto-recalc =======
  const handleDateWiseChange = (index, field, value) => {
    setDateWiseRows((prevRows) => {
      const updatedRows = normalizeDateWiseRows(prevRows).map(
        (row, rowIndex) => {
          if (rowIndex !== index) return row;
          const updated = { ...row, [field]: value };
          if (field === "kmStartUser") {
            updated.kmStartGarage = value;
          }
          if (field === "kmClose") {
            updated.kmCloseGarage = value;
          }
          // Recalculate row total KM
          if (field !== "totalKm") {
            const kmStartVal = firstFilledValue(
              updated.kmStartUser,
              updated.kmStartGarage,
            );
            const kmCloseVal = firstFilledValue(
              updated.kmClose,
              updated.kmCloseGarage,
            );
            if (hasInputValue(kmStartVal) && hasInputValue(kmCloseVal)) {
              const kmStart = Number(kmStartVal);
              const kmClose = Number(kmCloseVal);
              if (
                !Number.isNaN(kmStart) &&
                !Number.isNaN(kmClose) &&
                kmClose >= kmStart
              ) {
                updated.totalKm = kmClose - kmStart;
              } else {
                updated.totalKm = "";
              }
            } else {
              updated.totalKm = "";
            }
          }
          // Recalculate row total Time
          if (field !== "totalTime") {
            if (updated.openingTime && updated.closingTime) {
              const openMin = timePartsToMinutes(
                parseTimeStringToParts(updated.openingTime),
              );
              const closeMin = timePartsToMinutes(
                parseTimeStringToParts(updated.closingTime),
              );
              const diff = overnightAwareDiffMinutes(openMin, closeMin);
              updated.totalTime = formatMinutesToTotalTime(diff);
              updated.timeError = "";
            } else {
              updated.totalTime = "";
            }
          }
          return updated;
        },
      );

      // Sync header time select boxes if boundary times were edited in trip details
      if (field === "openingTime" && index === 0 && value) {
        const parts = parseTimeStringToParts(value);
        if (parts) setOpenTimeParts(parts);
      }
      if (field === "closingTime" && index === updatedRows.length - 1 && value) {
        const parts = parseTimeStringToParts(value);
        if (parts) setCloseTimeParts(parts);
      }

      // Update grand totals
      const summary = getRowsSummary(updatedRows);
      setDutyslip((prev) => ({
        ...prev,
        kmStart: summary.kmStart,
        kmEnd: summary.kmEnd,
        totalKm: summary.totalKm,
        totalTime: summary.totalTime,
      }));

      return updatedRows;
    });
  };

  const handleDateWiseKeyDown = (e, rowIndex, fieldName) => {
    if (e.key !== "Enter") return;
    e.preventDefault();

    const fieldOrder = [
      "kmStartUser",
      "kmClose",
      "tripDetails",
      "package",
    ];

    let nextField = null;
    if (fieldName === "kmClose" || fieldName === "totalKm" || fieldName === "totalTime") {
      nextField = "tripDetails";
    } else {
      const idx = fieldOrder.indexOf(fieldName);
      if (idx !== -1 && idx < fieldOrder.length - 1) {
        nextField = fieldOrder[idx + 1];
      }
    }

    if (nextField) {
      const nextEl = document.querySelector(
        `[data-dw-row="${rowIndex}"][data-dw-field="${nextField}"]`
      );
      if (nextEl) {
        nextEl.focus();
        if (typeof nextEl.select === "function") nextEl.select();
        return;
      }
    }

    // At row end (package)
    if (fieldName === "package") {
      if (rowIndex < dateWiseRows.length - 1) {
        // Next row already exists, focus its first column
        const nextRowEl = document.querySelector(
          `[data-dw-row="${rowIndex + 1}"][data-dw-field="kmStartUser"]`
        );
        if (nextRowEl) {
          nextRowEl.focus();
          if (typeof nextRowEl.select === "function") nextRowEl.select();
        }
      } else {
        // Last row: add new row and focus its kmStartUser
        addDateWiseRow();
        setTimeout(() => {
          const newRowEl = document.querySelector(
            `[data-dw-row="${rowIndex + 1}"][data-dw-field="kmStartUser"]`
          );
          if (newRowEl) {
            newRowEl.focus();
            if (typeof newRowEl.select === "function") newRowEl.select();
          }
        }, 80);
      }
    }
  };

  // ✅ Auto-fill all day rows when top package is selected
  const handleTopPackageChange = (packageId) => {
    setSelectedPackageId(packageId);
    const pkgObj = packages.find((p) => String(p.ID) === String(packageId));
    const pkgName = pkgObj?.PackageName || "";
    setDateWiseRows((prevRows) =>
      normalizeDateWiseRows(prevRows).map((row) => ({
        ...row,
        fareId: packageId || "",
        packageName: pkgName,
      }))
    );
  };

  const addDateWiseRow = () => {
    setDateWiseRows((prevRows) => {
      const rows = normalizeDateWiseRows(prevRows);
      const lastRow = rows[rows.length - 1];
      const nextDate = lastRow?.date ? getNextDate(lastRow.date) : toInputDate(getToday());
      const prevCloseKm = lastRow ? firstFilledValue(lastRow.kmClose, lastRow.kmCloseGarage) : "";
      const newRow = {
        ...createBlankDateWiseRow(nextDate),
        kmStartGarage: prevCloseKm || "",
        kmStartUser: prevCloseKm || "",
        openingTime: formatTimePartsToString(openTimeParts),
        closingTime: formatTimePartsToString(closeTimeParts),
      };
      const nextRows = [...rows, newRow];
      const summary = getRowsSummary(nextRows);
      setDutyslip((prev) => ({
        ...prev,
        closeDate: nextDate,
        kmStart: summary.kmStart,
        kmEnd: summary.kmEnd,
        totalKm: summary.totalKm,
        totalTime: summary.totalTime,
      }));
      return nextRows;
    });
  };

  const removeDateWiseRow = (index) => {
    setDateWiseRows((prevRows) => {
      const rows = normalizeDateWiseRows(prevRows);
      if (index !== rows.length - 1 || index === 0) return rows;
      const updatedRows = rows.filter((_, i) => i !== index);
      const summary = getRowsSummary(updatedRows);
      setDutyslip((prev) => ({
        ...prev,
        closeDate: updatedRows[updatedRows.length - 1]?.date || prev.closeDate,
        kmStart: summary.kmStart,
        kmEnd: summary.kmEnd,
        totalKm: summary.totalKm,
        totalTime: summary.totalTime,
      }));
      return updatedRows;
    });
  };

  const buildPdfRowFromForm = (id) => ({
    id,
    dutyslipDate: dutyslip.slipDate,
    endDutyslipDate: dutyslip.closeDate,
    time: buildTime24FromParts(openTimeParts),
    time12: `${openTimeParts.hh}:${openTimeParts.mm} ${openTimeParts.ap}`,
    endTime: buildTime24FromParts(closeTimeParts),
    endTime12: `${closeTimeParts.hh}:${closeTimeParts.mm} ${closeTimeParts.ap}`,
    partyName: dutyslip.partyName,
    partyAddress: dutyslip.address || dutyslip.clientAddress,
    guestName: dutyslip.clientName,
    mobile: dutyslip.clientMobile,
    fareId: selectedPackageId || null,
    packageName: packages.find((p) => String(p.ID) === String(selectedPackageId))?.PackageName || "",
    vehicleType: dutyslip.vehicleType,
    vehicleNo: dutyslip.vehicleNo || "",
    driverName: dutyslip.driverName,
    referedBy: dutyslip.referedBy || "",
    kmStart: dutyslip.kmStart,
    kmEnd: dutyslip.kmEnd,
    totalKm: dutyslip.totalKm,
    totalTime: dutyslip.totalTime,
    dateWiseDetails: buildDateWisePayload(),
  });

  const renderTimePicker = (index, field, currentValue) => {
    const parts = parseTimeStringToParts(currentValue);
    const update = (k, v) => {
      const updated = { ...parts, [k]: v };
      const timeStr = formatTimePartsToString(updated);
      handleDateWiseChange(index, field, timeStr);
      if (index === 0 && field === "openingTime") {
        setOpenTimeParts(updated);
        setDutyslip((p) => ({ ...p, openTime: timeStr }));
      }
    };
    const selectStyle = {
      border: "none",
      background: "transparent",
      fontSize: "12px",
      fontWeight: 600,
      color: "#0f172a",
      padding: "0 1px",
      cursor: "pointer",
      appearance: "none",
      WebkitAppearance: "none",
      textAlign: "center",
      outline: "none",
    };
    return (
      <div
        className="datewise_time_ui"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "2px",
          border: "1px solid #cbd5e1",
          borderRadius: "6px",
          padding: "4px 6px",
          background: "#ffffff",
          width: "100%",
          boxSizing: "border-box",
          height: "34px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
        }}
      >
        <select
          value={parts.hh}
          onChange={(e) => update("hh", e.target.value)}
          style={{ ...selectStyle, width: "22px" }}
        >
          {TIME_HOURS.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        <span style={{ color: "#94a3b8", fontSize: "12px", fontWeight: 700 }}>:</span>
        <select
          value={parts.mm}
          onChange={(e) => update("mm", e.target.value)}
          style={{ ...selectStyle, width: "22px" }}
        >
          {TIME_MINUTES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          value={parts.ap}
          onChange={(e) => update("ap", e.target.value)}
          style={{
            ...selectStyle,
            width: "32px",
            marginLeft: "2px",
            fontWeight: 700,
            color: "#0d9488",
          }}
        >
          <option>AM</option>
          <option>PM</option>
        </select>
      </div>
    );
  };

  const dwThStyle = {
    padding: "10px 8px",
    fontSize: "11px",
    fontWeight: 700,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    background: "#f8fafc",
    borderRight: "1px solid #cbd5e1",
    borderBottom: "2px solid #cbd5e1",
    whiteSpace: "nowrap",
    textAlign: "center",
  };

  const dwTdStyle = {
    padding: "6px 8px",
    borderRight: "1px solid #cbd5e1",
    borderBottom: "1px solid #cbd5e1",
    verticalAlign: "middle",
  };

  const dwInputStyle = {
    width: "100%",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    padding: "5px 8px",
    fontSize: "12px",
    fontWeight: "500",
    color: "#0f172a",
    background: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
    height: "34px",
    textAlign: "center",
    transition: "all 0.2s ease",
  };

  const renderDateWiseEditor = () => (
    <div className="datewise_section" style={{ marginTop: "10px", marginBottom: "4px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
        <span style={{ width: "4px", height: "16px", background: "#0d9488", borderRadius: "2px" }} />
        <span style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a" }}>Date Wise Trip Details</span>
      </div>
      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "12px" }}>
          <thead>
            <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
              <th style={{ padding: "10px 8px", fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center", whiteSpace: "nowrap" }}>Date</th>
              <th style={{ padding: "10px 8px", fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center", whiteSpace: "nowrap" }}>KM Start</th>
              <th style={{ padding: "10px 8px", fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center", whiteSpace: "nowrap" }}>KM Close</th>
              <th style={{ padding: "10px 8px", fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center", whiteSpace: "nowrap" }}>Total KM</th>
              <th style={{ padding: "10px 8px", fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center", whiteSpace: "nowrap" }}>Opening Time</th>
              <th style={{ padding: "10px 8px", fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center", whiteSpace: "nowrap" }}>Closing Time</th>
              <th style={{ padding: "10px 8px", fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center", whiteSpace: "nowrap" }}>Total Time</th>
              <th style={{ padding: "10px 8px", fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center", whiteSpace: "nowrap" }}>Trip Details</th>
              <th style={{ padding: "10px 8px", fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center", minWidth: "140px", whiteSpace: "nowrap" }}>Package</th>
              <th style={{ padding: "10px 8px", width: "30px" }}></th>
            </tr>
          </thead>
          <tbody>
            {dateWiseRows.map((row, index) => {
              const isLastRow = index === dateWiseRows.length - 1;
              return (
                <tr key={`${row.date}-${index}`} style={{ background: index % 2 === 1 ? "#f8fafc" : "#ffffff" }}>
                  <td style={{ padding: "6px", textAlign: "center" }}>
                    <div style={{ fontWeight: 700, fontSize: "12px", color: "#1d4ed8", background: "#eff6ff", border: "1px solid #dbeafe", borderRadius: "6px", padding: "5px 8px", whiteSpace: "nowrap" }}>
                      {formatDisplayDate(row.date)}
                    </div>
                  </td>
                  <td style={{ padding: "6px" }}>
                    <input
                      type="number"
                      value={row.kmStartUser}
                      onChange={(e) => handleDateWiseChange(index, "kmStartUser", e.target.value)}
                      data-dw-row={index}
                      data-dw-field="kmStartUser"
                      onKeyDown={(e) => handleDateWiseKeyDown(e, index, "kmStartUser")}
                      style={{ width: "100%", padding: "5px 8px", height: "34px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12px", fontWeight: "500", textAlign: "center", boxSizing: "border-box" }}
                    />
                  </td>
                  <td style={{ padding: "6px" }}>
                    <input
                      type="number"
                      value={row.kmClose}
                      onChange={(e) => handleDateWiseChange(index, "kmClose", e.target.value)}
                      data-dw-row={index}
                      data-dw-field="kmClose"
                      onKeyDown={(e) => handleDateWiseKeyDown(e, index, "kmClose")}
                      style={{ width: "100%", padding: "5px 8px", height: "34px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12px", fontWeight: "500", textAlign: "center", boxSizing: "border-box" }}
                    />
                  </td>
                  <td style={{ padding: "6px" }}>
                    <input
                      type="number"
                      value={row.totalKm}
                      onChange={(e) => handleDateWiseChange(index, "totalKm", e.target.value)}
                      data-dw-row={index}
                      data-dw-field="totalKm"
                      onKeyDown={(e) => handleDateWiseKeyDown(e, index, "totalKm")}
                      style={{ width: "100%", padding: "5px 8px", height: "34px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12px", fontWeight: 700, color: "#047857", background: "#f0fdf4", textAlign: "center", boxSizing: "border-box" }}
                    />
                  </td>
                  <td style={{ padding: "6px" }}>
                    {renderTimePicker(index, "openingTime", row.openingTime)}
                  </td>
                  <td style={{ padding: "6px" }}>
                    {renderTimePicker(index, "closingTime", row.closingTime)}
                  </td>
                  <td style={{ padding: "6px" }}>
                    <input
                      type="text"
                      value={row.totalTime || ""}
                      onChange={(e) => handleDateWiseChange(index, "totalTime", e.target.value)}
                      data-dw-row={index}
                      data-dw-field="totalTime"
                      onKeyDown={(e) => handleDateWiseKeyDown(e, index, "totalTime")}
                      placeholder="0"
                      style={{
                        width: "100%",
                        padding: "5px 8px",
                        height: "34px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "12px",
                        fontWeight: 700,
                        color: row.totalTime ? "#047857" : "#94a3b8",
                        background: row.totalTime ? "#ecfdf5" : "#ffffff",
                        textAlign: "center",
                        boxSizing: "border-box",
                      }}
                    />
                  </td>
                  <td style={{ padding: "6px" }}>
                    <input
                      type="text"
                      value={row.tripDetails}
                      onChange={(e) => handleDateWiseChange(index, "tripDetails", e.target.value)}
                      data-dw-row={index}
                      data-dw-field="tripDetails"
                      onKeyDown={(e) => handleDateWiseKeyDown(e, index, "tripDetails")}
                      placeholder="Enter trip details..."
                      style={{ width: "100%", padding: "5px 8px", height: "34px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12px", fontWeight: "500", boxSizing: "border-box" }}
                    />
                  </td>
                  <td style={{ padding: "6px" }}>
                    <select
                      value={row.fareId || selectedPackageId || ""}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selected = (packages || []).find((p) => p && String(p.ID) === String(selectedId));
                        const rawPkgName = String(selected?.PackageName || "").trim();
                        handleDateWiseChange(index, "fareId", selectedId);
                        handleDateWiseChange(index, "packageName", rawPkgName);
                        setSelectedPackageId(selectedId);
                      }}
                      data-dw-row={index}
                      data-dw-field="package"
                      onKeyDown={(e) => handleDateWiseKeyDown(e, index, "package")}
                      style={{ width: "100%", padding: "5px 8px", height: "34px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12px", fontWeight: "500", minWidth: "140px", cursor: "pointer", boxSizing: "border-box" }}
                    >
                      <option value="">-- Select Package --</option>
                      {Array.from(
                        new Map(
                          (packages || [])
                            .filter((p) => p && p.PackageName && p.ID != null)
                            .map((p) => [String(p.PackageName).trim().toLowerCase(), p])
                        ).values()
                      ).map((p) => {
                        const pkgName = String(p.PackageName || "").trim();
                        return (
                          <option key={p.ID || Math.random()} value={p.ID}>
                            {pkgName || "-- Package --"}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                  <td style={{ padding: "6px", textAlign: "center" }}>
                    {isLastRow && index > 0 && (
                      <button
                        type="button"
                        onClick={() => removeDateWiseRow(index)}
                        title="Remove this date row"
                        style={{
                          width: "24px",
                          height: "24px",
                          lineHeight: "22px",
                          borderRadius: "50%",
                          border: "1px solid #fecaca",
                          background: "#fef2f2",
                          color: "#ef4444",
                          fontWeight: 700,
                          fontSize: "12px",
                          cursor: "pointer",
                          padding: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.15s ease",
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f8fafc", borderTop: "2px solid #e2e8f0" }}>
              <td
                colSpan={5}
                style={{
                  padding: "10px 12px",
                  textAlign: "right",
                  fontWeight: 700,
                  fontSize: "12.5px",
                  color: "#0f172a",
                }}
              >
                Grand Total
              </td>
              <td
                style={{
                  padding: "10px 6px",
                  textAlign: "center",
                  fontWeight: 700,
                  fontSize: "12.5px",
                  color: "#0f172a",
                }}
              >
                {dutyslip.totalKm || 0}
              </td>
              <td colSpan={2} style={{ padding: "10px 6px" }}></td>
              <td
                style={{
                  padding: "10px 6px",
                  textAlign: "center",
                  fontWeight: 700,
                  fontSize: "12.5px",
                  color: "#0f172a",
                }}
              >
                {dutyslip.totalTime !== "" && dutyslip.totalTime !== undefined && dutyslip.totalTime !== null ? dutyslip.totalTime : "0h"}
              </td>
              <td colSpan={2} style={{ padding: "10px 6px" }}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );

  // ===================== COMPANY & PREVIEW HELPERS =====================
  const fetchCompanyInfo = async (compId) => {
    const selectedCompany =
      JSON.parse(localStorage.getItem("selectedCompany")) || {};
    const companyId =
      compId ||
      localStorage.getItem("companyId") ||
      selectedCompany.id ||
      selectedCompany.CompID ||
      selectedCompanyId;

    let companyData = {};
    let companyLogoUrl = null;

    if (companyId) {
      try {
        const companyRes = await axios.get(
          `${BASE_API_URL}/invoice/company/${companyId}`,
          { timeout: 5000 },
        );
        if (companyRes.data?.success) {
          companyData = companyRes.data.data || {};
        }
      } catch (err) {
        console.warn("Failed to fetch company info for duty slip", err);
      }

      try {
        const restCompanyData = await fetchRestCompany(companyId);
        companyData = {
          ...restCompanyData,
          ...companyData,
          LogoPath:
            companyData.LogoPath ||
            companyData.Logopath ||
            companyData.logoPath ||
            restCompanyData.LogoPath,
        };
      } catch (err) {
        console.warn("Failed to fetch company logo for duty slip", err);
      }
    }

    const companyName =
      companyData.Name ||
      companyData.name ||
      selectedCompany.Name ||
      selectedCompany.name ||
      "DAZY TOURE TRAVEL'S";
    const companyAddress =
      companyData.Address ||
      companyData.address ||
      selectedCompany.Address ||
      selectedCompany.address ||
      "Sahakar Marg";
    companyLogoUrl = buildLogoUrl(
      companyData.LogoPath ||
        companyData.Logopath ||
        companyData.logoPath ||
        selectedCompany?.LogoPath ||
        selectedCompany?.Logopath ||
        selectedCompany?.logoPath
    );

    const companyColor =
      companyData.CompanyColourCode ||
      companyData.companyColourCode ||
      selectedCompany?.CompanyColourCode ||
      "#000000";

    return { companyName, companyAddress, companyLogoUrl, companyColor, companyData };
  };

  const normalizeDutySlipForView = (row) => {
    const slipId = row.id || row.DutyslipID || "";
    const slipDate = row.dutyslipDate || row.slipDate || "";
    const journeyStartDate =
      row.openDate ||
      row.openDutyslipDate ||
      row.OpeningDate ||
      (Array.isArray(row.dateWiseDetails) && row.dateWiseDetails.length > 0
        ? row.dateWiseDetails[0].date || row.dateWiseDetails[0].TripDate
        : null) ||
      row.dutyslipDate ||
      row.slipDate ||
      "";
    const endDate = row.endDutyslipDate || "";
    const startTime = row.time12 || (row.time ? format12Hour(row.time) : "");
    const endTime = row.endTime12 || (row.endTime ? format12Hour(row.endTime) : "");
    const kmStart = row.kmStart != null ? row.kmStart : "";
    const kmEnd = row.kmEnd != null ? row.kmEnd : "";
    const partyName = row.partyName || row.PartyName || "";
    const partyAddress =
      row.ClientAddress ||
      row.clientAddress ||
      row.address ||
      row.Address ||
      row.partyAddress ||
      row.PartyAddress ||
      "";
    const guestName = row.guestName || row.ClientName || row.clientName || "";
    const hiredMobile =
      row.partyMobile ||
      row.hiredMobile ||
      row.partyPhone ||
      row.mobile ||
      row.Mobile ||
      "";
    const selectedPkg = packages.find(
      (p) => String(p.ID) === String(row.fareId || row.FareId || selectedPackageId),
    );
    const packageName =
      row.packageName ||
      row.PackageName ||
      (selectedPkg ? selectedPkg.PackageName : "");
    const vehicleType = row.vehicleType || row.VehicleType || row.CarType || "";
    const vehicleNo = row.vehicleNo || row.VehicleNo || "";
    const taxiDisplay =
      vehicleType && vehicleNo && vehicleType !== vehicleNo
        ? `${vehicleType} - ${vehicleNo}`
        : vehicleType || vehicleNo || "";
    const driverName = row.driverName || row.DriverName || "";
    const totalKm = row.totalKm != null && row.totalKm !== "" ? row.totalKm : "";

    let totalTime = "";
    if (typeof row.totalTime === "string") {
      totalTime = row.totalTime;
    } else if (row.totalTime) {
      let h = Math.floor(row.totalTime);
      let m = Math.round((row.totalTime % 1) * 60);
      if (m === 60) {
        h += 1;
        m = 0;
      }
      totalTime = m > 0 ? `${h}h ${m}m` : `${h}h`;
    }

    const tripRows = row.dateWiseDetails?.length
      ? normalizeDateWiseRows(row.dateWiseDetails)
      : mergeDateWiseRows(journeyStartDate, endDate, [], {
          kmStart,
          kmEnd,
          totalKm,
          openingTime: startTime === "-" ? "" : startTime,
          closingTime: endTime === "-" ? "" : endTime,
        });

    return {
      slipId,
      slipDate,
      journeyStartDate,
      endDate,
      startTime,
      endTime,
      partyName,
      partyAddress,
      guestName,
      hiredMobile,
      taxiDisplay,
      vehicleType,
      vehicleNo,
      driverName,
      referedBy: row.referedBy || row.ReferedBy || "",
      packageName,
      totalKm,
      totalTime,
      tripRows,
    };
  };

  const handlePreviewDutyslip = async (row) => {
    try {
      const compInfo = await fetchCompanyInfo(selectedCompanyId);
      const normalized = normalizeDutySlipForView(row);
      setPreviewCompany(compInfo);
      setPreviewData(normalized);
      setShowPreviewModal(true);
    } catch (err) {
      console.error("Preview error:", err);
      showToast.error("Failed to open preview");
    }
  };

  const handlePrintPreview = () => {
    const printContent = document.getElementById("duty-slip-preview-render");
    if (!printContent) return;
    const printWindow = window.open("", "_blank", "width=1100,height=800");
    if (!printWindow) {
      showToast.warn("Please allow popups to print");
      return;
    }
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Duty Slip ${previewData?.slipId || ""}</title>
          <style>
            @page { size: landscape; margin: 8mm; }
            body { margin: 0; padding: 12px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; background: #fff; color: #0f172a; }
            * { box-sizing: border-box; }
            table { border-collapse: collapse; width: 100%; table-layout: fixed; }
            th, td { border: 1px solid #000; padding: 6px 4px; text-align: center; font-size: 12px; }
            th { background-color: #fff; font-weight: 700; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <script>
            window.onload = function() {
              window.print();
              setTimeout(() => { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // ===================== FULL PDF GENERATION =====================
  const handleDownloadDutyslip = async (row) => {
    try {
      await downloadDutySlip(row);
    } catch (err) {
      console.error("Duty Slip PDF ERROR:", err);
      showToast.error("Failed to download duty slip PDF!");
    }
  };
  // ===================== END PDF GENERATION =====================

  const openNewDutyslip = () => {
    setEditId(null);
    setIsEndManual(false);
    setOpenTimeParts({ hh: "12", mm: "00", ap: "AM" });
    setCloseTimeParts({ hh: "08", mm: "00", ap: "AM" });
    setTimeParts({ hh: "12", mm: "00", ap: "AM" });
    setEndTimeParts({ hh: "08", mm: "00", ap: "AM" });
    const startTime = "00:00:00";
    const initialDate = selectedDate || getToday();
    setDutyslip({
      slipDate: initialDate,
      openDate: initialDate,
      closeDate: initialDate,
      totalTime: "",
      partyName: "",
      address: "",
      clientAddress: "",
      clientMobile: "",
      clientName: "",
      vehicleType: "",
      acType: "AC",
      driverName: "",
      referedBy: "",
      kmStart: "",
      kmEnd: "",
      totalKm: "",
      bookingId: null,
    });
    const initialRows = mergeDateWiseRows(today, today, []);
    setDateWiseRows(initialRows);
    autoCalculateEnd(today, startTime);
    setShowAddModal(true);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const filteredDutyslip = dutyslipList.filter((b) => {
    if (filterPartyName && b.partyName?.toLowerCase() !== filterPartyName.toLowerCase()) {
      return false;
    }
    if (filterFromDate && b.dutyslipDate && b.dutyslipDate < filterFromDate) {
      return false;
    }
    if (filterToDate && b.dutyslipDate && b.dutyslipDate > filterToDate) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filterFromDate, filterToDate, filterPartyName, dutyslipList]);

  const totalPages = Math.ceil(filteredDutyslip.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDutyslip = filteredDutyslip.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="dutyslip_container">

      {/* TOP BAR */}
      <div className="dutyslip_top">
        <div className="dutyslip_date_inline">
          <span className="dutyslip_date_label">From Date</span>
          <input
            type="date"
            className="dutyslip_date_input"
            value={filterFromDate}
            onChange={(e) => setFilterFromDate(e.target.value)}
          />
        </div>

        <div className="dutyslip_date_inline">
          <span className="dutyslip_date_label">To Date</span>
          <input
            type="date"
            className="dutyslip_date_input"
            value={filterToDate}
            onChange={(e) => setFilterToDate(e.target.value)}
          />
        </div>

        <div className="dutyslip_date_inline">
          <span className="dutyslip_date_label">Party / Company</span>
          <SearchableSelect
            options={[
              { value: "", label: "All Parties" },
              ...parties.map((p) => ({ value: p.HEADName, label: p.HEADName })),
            ]}
            value={filterPartyName}
            onChange={(val) => setFilterPartyName(val || "")}
            placeholder="All Parties"
            width="200px"
            height="28px"
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <button
            type="button"
            onClick={() => {
              setFilterFromDate(getCurrentMonthFirstDate());
              setFilterToDate(today);
              setFilterPartyName("");
            }}
            title="Reset to current month"
            style={{
              height: "28px",
              padding: "0 14px",
              background: "#008b8b",
              color: "#ffffff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "11.5px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              transition: "0.15s",
              boxShadow: "0 1px 2px rgba(0, 139, 139, 0.15)",
            }}
          >
            Reset
          </button>

          {(filterFromDate || filterToDate || filterPartyName) && (
            <button
              type="button"
              onClick={() => {
                setFilterFromDate("");
                setFilterToDate("");
                setFilterPartyName("");
              }}
              title="Clear all filters to view all duty slips"
              style={{
                height: "28px",
                padding: "0 12px",
                background: "#f1f5f9",
                color: "#475569",
                border: "1px solid #cbd5e0",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "11.5px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                transition: "0.15s",
              }}
            >
              Clear All
            </button>
          )}
        </div>

        <button className="new_dutyslip_btn" style={{ marginLeft: "auto" }} onClick={openNewDutyslip}>
          + New Duty Slip
        </button>
      </div>

      {/* LIST TABLE */}
      <div className="dutyslip_list_section">
        <h6>Duty slip List</h6>
        <table className="dutyslip_table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Duty Slip</th>
              <th>Party</th>
              <th>Guest Name</th>
              <th>Guest Mobile</th>
              <th>Vehicle</th>
              <th>Driver Name</th>
              <th>Total Time</th>
              <th>Total KM</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoadingDutyslip ? (
              <TableRowsSkeleton columns={10} rows={5} />
            ) : filteredDutyslip.length === 0 ? (
              <tr>
                <td colSpan="10" className="no_data">
                  No duty slip found
                </td>
              </tr>
            ) : (
              paginatedDutyslip.map((b) => {
                const isInvoiceMade = Boolean(
                  b.isInvoiceMade ||
                  b.invoiceId ||
                  b.invoiceNo ||
                  (uninvoicedLoaded && !uninvoicedSlipIds.has(Number(b.id)))
                );
                return (
                  <tr
                    key={b.id}
                    className={!isInvoiceMade ? "uninvoiced_duty_slip_row" : ""}
                    style={{
                      background: !isInvoiceMade ? "#fffbeb" : undefined,
                    }}
                  >
                    <td>{b.dutyslipDate ? formatDisplayDate(b.dutyslipDate) : "-"}</td>
                    <td>{b.id || "-"}</td>
                    <td>{b.partyName || "-"}</td>
                    <td>{b.guestName || "-"}</td>
                    <td>{b.mobile || "-"}</td>
                    <td>{b.vehicleType || "-"}</td>
                    <td>{b.driverName || "-"}</td>
                    <td>
                      {b.totalTime
                        ? `${Math.floor(b.totalTime)}h ${Math.round((b.totalTime % 1) * 60)}m`
                        : "-"}
                    </td>
                    <td>{b.totalKm ?? "-"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button
                        type="button"
                        className="action_btn preview_btn"
                        title="Preview Duty Slip"
                        onClick={() => handlePreviewDutyslip(b)}
                      >
                        <i className="fa-solid fa-eye"></i>
                      </button>
                      <button
                        type="button"
                        className="action_btn edit_btn"
                        title="Edit Duty Slip"
                        onClick={() => openEditDutyslip(b)}
                      >
                        <i className="fa-solid fa-pen-to-square"></i>
                      </button>
                      <button
                        type="button"
                        className="action_btn delete_btn"
                        title="Delete Duty Slip"
                        onClick={() => openDeleteModal(b)}
                      >
                        <i className="fa-solid fa-trash-can"></i>
                      </button>
                      <button
                        type="button"
                        className="action_btn download_btn"
                        title="Download Duty Slip PDF"
                        onClick={() => handleDownloadDutyslip(b)}
                      >
                        <i className="fa-solid fa-download"></i>
                      </button>
                      {isInvoiceMade ? (
                        <button
                          type="button"
                          className="action_btn inv_made_btn"
                          title={
                            b.invoiceNo
                              ? `Invoice Already Made (#${b.invoiceNo}) - Click to view`
                              : "Invoice Already Made - Click to view"
                          }
                          onClick={() => handleViewInvoice(b)}
                        >
                          <i className="fa-solid fa-file-circle-check"></i>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="action_btn create_inv_btn"
                          title="Create Invoice"
                          onClick={() => handleCreateInvoice(b)}
                        >
                          <i className="fa-solid fa-file-invoice"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {filteredDutyslip.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              background: "#fff",
              borderTop: "1px solid #e2e8f0",
              borderBottomLeftRadius: "8px",
              borderBottomRightRadius: "8px",
              fontSize: "13px",
              color: "#475569",
            }}
          >
            <div>
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredDutyslip.length)} of {filteredDutyslip.length} entries
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "5px 12px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  background: currentPage === 1 ? "#f1f5f9" : "#fff",
                  color: currentPage === 1 ? "#94a3b8" : "#334155",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontWeight: 500,
                  fontSize: "13px",
                }}
              >
                Previous
              </button>
              <span style={{ fontWeight: 600, padding: "0 6px" }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage >= totalPages}
                style={{
                  padding: "5px 12px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  background: currentPage >= totalPages ? "#f1f5f9" : "#fff",
                  color: currentPage >= totalPages ? "#94a3b8" : "#334155",
                  cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                  fontWeight: 500,
                  fontSize: "13px",
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* DELETE CONFIRM MODAL */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteDutyslip}
        title="Delete Duty Slip?"
        message="Are you sure you want to delete this duty slip record? This action cannot be undone."
        itemName={deleteItemName}
        loading={isDeleting}
      />

      {/* ADD MODAL */}
      {showAddModal && (
        <div className="dutyslip_modal">
          <div
            className="dutyslip_modal_content"
            style={{
              width: "96vw",
              maxWidth: "1500px",
              height: "auto",
              maxHeight: "92vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <div
              className="modal_header"
              style={{ padding: "12px 24px", flexShrink: 0 }}
            >
              <h5>New Duty Slip</h5>
              <span
                className="close_btn"
                onClick={() => setShowAddModal(false)}
              >
                ✕
              </span>
            </div>

            <div
              className="dutyslip_form_scroll"
              style={{
                maxHeight: "calc(92vh - 110px)",
                overflowY: "auto",
                padding: "12px 24px 8px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* COMPACT FIELDS (Invoice Creation style) */}
              <div className="dutyslip-fields-container">
                {/* Row 1 */}
                <div className="dutyslip-fields-row">
                  <div className="dutyslip_field" style={{ width: "145px", flexShrink: 0 }}>
                    <label>Duty Slip Date</label>
                    <input
                      type="date"
                      name="slipDate"
                      value={dutyslip.slipDate}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "292px", flexShrink: 0 }}>
                    <label>Party Name</label>
                    <SearchableSelect
                      options={parties.map((p) => ({ value: p.HEADName, label: p.HEADName }))}
                      value={dutyslip.partyName}
                      onChange={(val) => handlePartySelect({ target: { value: val || "" } })}
                      placeholder="Select Party"
                      height="28px"
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "210px", flexShrink: 0 }}>
                    <label>Referred By</label>
                    <input
                      type="text"
                      name="referedBy"
                      value={dutyslip.referedBy || ""}
                      onChange={handleChange}
                      maxLength={50}
                      placeholder="Referred By"
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "285px", flexShrink: 0 }}>
                    <label>Driver Name</label>
                    <input
                      name="driverName"
                      value={dutyslip.driverName}
                      onChange={handleChange}
                      placeholder="Enter driver name"
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "285px", flexShrink: 0 }}>
                    <label>Address</label>
                    <input
                      type="text"
                      name="address"
                      value={dutyslip.address}
                      onChange={handleChange}
                      maxLength={255}
                      placeholder="Enter address"
                    />
                  </div>
                </div>

                {/* Row 2 */}
                <div className="dutyslip-fields-row">
                  <div className="dutyslip_field" style={{ width: "145px", flexShrink: 0 }}>
                    <label>Guest Name</label>
                    <input
                      name="clientName"
                      value={dutyslip.clientName}
                      onChange={handleChange}
                      placeholder="Enter guest name"
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "130px", flexShrink: 0 }}>
                    <label>Guest Mobile</label>
                    <input
                      type="text"
                      name="clientMobile"
                      value={dutyslip.clientMobile}
                      onChange={handleChange}
                      placeholder="Enter mobile"
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "150px", flexShrink: 0 }}>
                    <label>Vehicle Type</label>
                    <SearchableSelect
                      options={allVehicleTypes}
                      value={dutyslip.vehicleType}
                      onChange={(val) => {
                        setDutyslip((p) => ({ ...p, vehicleType: val || "", vehicleNo: "" }));
                      }}
                      placeholder="Select Type"
                      height="28px"
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "210px", flexShrink: 0 }}>
                    <label>Vehicle Number</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <SearchableSelect
                          options={(() => {
                            const filtered = vehicles.filter((v) =>
                              !dutyslip.vehicleType ||
                              (v.VehicleTypeName && v.VehicleTypeName.toLowerCase() === dutyslip.vehicleType.toLowerCase()) ||
                              (v.VehicleType && v.VehicleType.toLowerCase() === dutyslip.vehicleType.toLowerCase())
                            );
                            const hasCurrent = dutyslip.vehicleNo && filtered.some((v) => v.VehicleNo === dutyslip.vehicleNo);
                            const opts = [];
                            if (dutyslip.vehicleNo && !hasCurrent) {
                              opts.push({ value: dutyslip.vehicleNo, label: dutyslip.vehicleNo });
                            }
                            filtered.forEach((v) => {
                              opts.push({ value: v.VehicleNo, label: v.VehicleNo });
                            });
                            return opts;
                          })()}
                          value={dutyslip.vehicleNo || ""}
                          onChange={(val) => {
                            handleChange({ target: { name: "vehicleNo", value: val || "" } });
                          }}
                          placeholder={dutyslip.vehicleType ? "Select Vehicle No" : "Select Type first"}
                          isDisabled={!dutyslip.vehicleType}
                          height="28px"
                        />
                      </div>
                      <button
                        type="button"
                        title="Add new vehicle number"
                        onClick={() => openAddVehicleModalForDutySlip(dutyslip.vehicleType)}
                        style={{
                          background: "#0d6efd",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          padding: "0 8px",
                          cursor: "pointer",
                          fontWeight: "600",
                          fontSize: "11px",
                          lineHeight: 1,
                          whiteSpace: "nowrap",
                          height: "28px",
                          display: "inline-flex",
                          alignItems: "center",
                          boxSizing: "border-box",
                        }}
                      >
                        + Add
                      </button>
                    </div>
                  </div>

                  <div className="dutyslip_field" style={{ width: "380px", flexShrink: 0 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <i className="fa-regular fa-clock" style={{ color: "#0d9488", fontSize: "11px" }}></i>
                      Reporting Date & Time
                    </label>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <input
                        type="date"
                        name="openDate"
                        value={dutyslip.openDate}
                        onChange={handleChange}
                        style={{
                          width: "140px",
                          flex: "0 0 140px",
                          height: "28px",
                          border: "1px solid #cbd5e1",
                          borderRadius: "4px",
                          padding: "0 8px",
                          fontSize: "12px",
                          fontWeight: "500",
                          background: "#ffffff",
                          color: "#1e293b",
                          boxSizing: "border-box",
                        }}
                      />
                      <div
                        className="modern_time_capsule"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          height: "28px",
                          border: "1px solid #cbd5e1",
                          borderRadius: "4px",
                          background: "#f8fafc",
                          padding: "0 6px",
                          gap: "2px",
                          boxSizing: "border-box",
                        }}
                      >
                        <select
                          className="time_select"
                          value={openTimeParts.hh}
                          onChange={(e) => updateOpenTime("hh", e.target.value)}
                          style={{
                            border: "none",
                            background: "transparent",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#0f172a",
                            width: "44px",
                            minWidth: "44px",
                            padding: "0 2px 0 4px",
                            cursor: "pointer",
                            outline: "none",
                            textAlign: "left",
                          }}
                        >
                          {TIME_HOURS.map((v) => (
                            <option key={v}>{v}</option>
                          ))}
                        </select>
                        <span style={{ color: "#0d9488", fontWeight: 700, fontSize: "12px", lineHeight: 1 }}>:</span>
                        <select
                          className="time_select"
                          value={openTimeParts.mm}
                          onChange={(e) => updateOpenTime("mm", e.target.value)}
                          style={{
                            border: "none",
                            background: "transparent",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#0f172a",
                            width: "44px",
                            minWidth: "44px",
                            padding: "0 2px 0 4px",
                            cursor: "pointer",
                            outline: "none",
                            textAlign: "left",
                          }}
                        >
                          {TIME_MINUTES.map((v) => (
                            <option key={v}>{v}</option>
                          ))}
                        </select>
                        <select
                          className="time_select ampm_select"
                          value={openTimeParts.ap}
                          onChange={(e) => updateOpenTime("ap", e.target.value)}
                          style={{
                            border: "none",
                            background: "#ccfbf1",
                            color: "#0f766e",
                            fontSize: "11px",
                            fontWeight: 700,
                            width: "54px",
                            minWidth: "54px",
                            padding: "2px 4px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            outline: "none",
                            marginLeft: "3px",
                          }}
                        >
                          <option>AM</option>
                          <option>PM</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Date Wise Trip Details */}
              <div style={{ marginTop: "4px" }}>
                {renderDateWiseEditor()}
              </div>
            </div>

            {/* Sticky Footer with Add Date Row (left) and Save (right) */}
            <div
              className="modal_footer"
              style={{
                padding: "8px 24px",
                background: "#fff",
                borderTop: "1px solid #e2e8f0",
                flexShrink: 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                type="button"
                onClick={addDateWiseRow}
                style={{
                  padding: "4px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#0f766e",
                  background: "#ecfdf5",
                  border: "1px solid #99f6e4",
                  borderRadius: "6px",
                  cursor: "pointer",
                  height: "32px",
                }}
              >
                + Add Date Row
              </button>

              <button
                className="dutyslip_submit_btn"
                onClick={() => saveDutyslip(false)}
                disabled={isSaving}
                style={{
                  width: "auto",
                  height: "36px",
                  padding: "0 16px",
                  background: "#0f766e",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "background 0.2s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#0d5e57")}
                onMouseLeave={(e) => (e.target.style.background = "#0f766e")}
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <div className="dutyslip_modal">
          <div
            className="dutyslip_modal_content"
            style={{
              width: "96vw",
              maxWidth: "1500px",
              height: "auto",
              maxHeight: "92vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <div
              className="modal_header"
              style={{ padding: "12px 24px", flexShrink: 0 }}
            >
              <h5>Edit Duty Slip</h5>
              <span
                className="close_btn"
                onClick={() => setShowEditModal(false)}
              >
                ✕
              </span>
            </div>

            <div
              className="dutyslip_form_scroll"
              style={{
                maxHeight: "calc(92vh - 110px)",
                overflowY: "auto",
                padding: "12px 24px 8px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* COMPACT FIELDS (Invoice Creation style) */}
              <div className="dutyslip-fields-container">
                {/* Row 1 */}
                <div className="dutyslip-fields-row">
                  <div className="dutyslip_field" style={{ width: "145px", flexShrink: 0 }}>
                    <label>Duty Slip Date</label>
                    <input
                      type="date"
                      name="slipDate"
                      value={dutyslip.slipDate}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "292px", flexShrink: 0 }}>
                    <label>Party Name</label>
                    <SearchableSelect
                      options={parties.map((p) => ({ value: p.HEADName, label: p.HEADName }))}
                      value={dutyslip.partyName}
                      onChange={(val) => handlePartySelect({ target: { value: val || "" } })}
                      placeholder="Select Party"
                      height="28px"
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "210px", flexShrink: 0 }}>
                    <label>Referred By</label>
                    <input
                      type="text"
                      name="referedBy"
                      value={dutyslip.referedBy || ""}
                      onChange={handleChange}
                      maxLength={50}
                      placeholder="Referred By"
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "285px", flexShrink: 0 }}>
                    <label>Driver Name</label>
                    <input
                      name="driverName"
                      value={dutyslip.driverName}
                      onChange={handleChange}
                      placeholder="Enter driver name"
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "285px", flexShrink: 0 }}>
                    <label>Address</label>
                    <input
                      type="text"
                      name="address"
                      value={dutyslip.address}
                      onChange={handleChange}
                      maxLength={255}
                      placeholder="Enter address"
                    />
                  </div>
                </div>

                {/* Row 2 */}
                <div className="dutyslip-fields-row">
                  <div className="dutyslip_field" style={{ width: "145px", flexShrink: 0 }}>
                    <label>Guest Name</label>
                    <input
                      name="clientName"
                      value={dutyslip.clientName}
                      onChange={handleChange}
                      placeholder="Enter guest name"
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "130px", flexShrink: 0 }}>
                    <label>Guest Mobile</label>
                    <input
                      type="text"
                      name="clientMobile"
                      value={dutyslip.clientMobile}
                      onChange={handleChange}
                      placeholder="Enter mobile"
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "150px", flexShrink: 0 }}>
                    <label>Vehicle Type</label>
                    <SearchableSelect
                      options={allVehicleTypes}
                      value={dutyslip.vehicleType}
                      onChange={(val) => {
                        setDutyslip((p) => ({ ...p, vehicleType: val || "", vehicleNo: "" }));
                      }}
                      placeholder="Select Type"
                      height="28px"
                    />
                  </div>

                  <div className="dutyslip_field" style={{ width: "210px", flexShrink: 0 }}>
                    <label>Vehicle Number</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <SearchableSelect
                          options={(() => {
                            const filtered = vehicles.filter((v) =>
                              !dutyslip.vehicleType ||
                              (v.VehicleTypeName && v.VehicleTypeName.toLowerCase() === dutyslip.vehicleType.toLowerCase()) ||
                              (v.VehicleType && v.VehicleType.toLowerCase() === dutyslip.vehicleType.toLowerCase())
                            );
                            const hasCurrent = dutyslip.vehicleNo && filtered.some((v) => v.VehicleNo === dutyslip.vehicleNo);
                            const opts = [];
                            if (dutyslip.vehicleNo && !hasCurrent) {
                              opts.push({ value: dutyslip.vehicleNo, label: dutyslip.vehicleNo });
                            }
                            filtered.forEach((v) => {
                              opts.push({ value: v.VehicleNo, label: v.VehicleNo });
                            });
                            return opts;
                          })()}
                          value={dutyslip.vehicleNo || ""}
                          onChange={(val) => {
                            handleChange({ target: { name: "vehicleNo", value: val || "" } });
                          }}
                          placeholder={dutyslip.vehicleType ? "Select Vehicle No" : "Select Type first"}
                          isDisabled={!dutyslip.vehicleType}
                          height="28px"
                        />
                      </div>
                      <button
                        type="button"
                        title="Add new vehicle number"
                        onClick={() => openAddVehicleModalForDutySlip(dutyslip.vehicleType)}
                        style={{
                          background: "#0d6efd",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          padding: "0 8px",
                          cursor: "pointer",
                          fontWeight: "600",
                          fontSize: "11px",
                          lineHeight: 1,
                          whiteSpace: "nowrap",
                          height: "28px",
                          display: "inline-flex",
                          alignItems: "center",
                          boxSizing: "border-box",
                        }}
                      >
                        + Add
                      </button>
                    </div>
                  </div>

                  <div className="dutyslip_field" style={{ width: "380px", flexShrink: 0 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <i className="fa-regular fa-clock" style={{ color: "#0d9488", fontSize: "11px" }}></i>
                      Reporting Date & Time
                    </label>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <input
                        type="date"
                        name="openDate"
                        value={dutyslip.openDate}
                        onChange={handleChange}
                        style={{
                          width: "140px",
                          flex: "0 0 140px",
                          height: "28px",
                          border: "1px solid #cbd5e1",
                          borderRadius: "4px",
                          padding: "0 8px",
                          fontSize: "12px",
                          fontWeight: "500",
                          background: "#ffffff",
                          color: "#1e293b",
                          boxSizing: "border-box",
                        }}
                      />
                      <div
                        className="modern_time_capsule"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          height: "28px",
                          border: "1px solid #cbd5e1",
                          borderRadius: "4px",
                          background: "#f8fafc",
                          padding: "0 6px",
                          gap: "2px",
                          boxSizing: "border-box",
                        }}
                      >
                        <select
                          className="time_select"
                          value={openTimeParts.hh}
                          onChange={(e) => updateOpenTime("hh", e.target.value)}
                          style={{
                            border: "none",
                            background: "transparent",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#0f172a",
                            width: "44px",
                            minWidth: "44px",
                            padding: "0 2px 0 4px",
                            cursor: "pointer",
                            outline: "none",
                            textAlign: "left",
                          }}
                        >
                          {TIME_HOURS.map((v) => (
                            <option key={v}>{v}</option>
                          ))}
                        </select>
                        <span style={{ color: "#0d9488", fontWeight: 700, fontSize: "12px", lineHeight: 1 }}>:</span>
                        <select
                          className="time_select"
                          value={openTimeParts.mm}
                          onChange={(e) => updateOpenTime("mm", e.target.value)}
                          style={{
                            border: "none",
                            background: "transparent",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#0f172a",
                            width: "44px",
                            minWidth: "44px",
                            padding: "0 2px 0 4px",
                            cursor: "pointer",
                            outline: "none",
                            textAlign: "left",
                          }}
                        >
                          {TIME_MINUTES.map((v) => (
                            <option key={v}>{v}</option>
                          ))}
                        </select>
                        <select
                          className="time_select ampm_select"
                          value={openTimeParts.ap}
                          onChange={(e) => updateOpenTime("ap", e.target.value)}
                          style={{
                            border: "none",
                            background: "#ccfbf1",
                            color: "#0f766e",
                            fontSize: "11px",
                            fontWeight: 700,
                            width: "54px",
                            minWidth: "54px",
                            padding: "2px 4px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            outline: "none",
                            marginLeft: "3px",
                          }}
                        >
                          <option>AM</option>
                          <option>PM</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: "4px" }}>
                {renderDateWiseEditor()}
              </div>
            </div>

            {/* Sticky Footer with Add Date Row (left) and Update (right) */}
            <div
              className="modal_footer"
              style={{
                padding: "8px 24px",
                background: "#fff",
                borderTop: "1px solid #e2e8f0",
                flexShrink: 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                type="button"
                onClick={addDateWiseRow}
                style={{
                  padding: "4px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#0f766e",
                  background: "#ecfdf5",
                  border: "1px solid #99f6e4",
                  borderRadius: "6px",
                  cursor: "pointer",
                  height: "32px",
                }}
              >
                + Add Date Row
              </button>

              <button
                className="dutyslip_submit_btn"
                onClick={() => updateDutyslip(false)}
                disabled={isSaving}
                style={{
                  width: "auto",
                  height: "36px",
                  padding: "0 16px",
                  background: "#0f766e",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "background 0.2s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#0d5e57")}
                onMouseLeave={(e) => (e.target.style.background = "#0f766e")}
              >
                {isSaving ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ================= ADD VEHICLE MINI-MODAL ================= */}
      {showAddVehicleModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.55)",
            backdropFilter: "blur(4px)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "14px",
              padding: "28px 32px",
              width: "100%",
              maxWidth: "460px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              position: "relative",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: "17px", color: "#1e293b" }}>
                🚗 Add New Vehicle
              </h5>
              <span
                onClick={() => setShowAddVehicleModal(false)}
                style={{ cursor: "pointer", fontSize: "20px", color: "#64748b", lineHeight: 1 }}
              >
                ✕
              </span>
            </div>

            {/* Vehicle Type Name */}
            <div style={{ marginBottom: "14px" }}>
              <label
                style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "5px" }}
              >
                Vehicle Type Name *
              </label>
              <select
                value={newVehicleForm.isCustomType ? "__NEW__" : newVehicleForm.vtypeName}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "__NEW__") {
                    setNewVehicleForm((p) => ({ ...p, vtypeName: "", isCustomType: true }));
                  } else {
                    setNewVehicleForm((p) => ({ ...p, vtypeName: val, isCustomType: false }));
                  }
                }}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                  background: "#fff",
                  marginBottom: newVehicleForm.isCustomType ? "8px" : "0",
                }}
              >
                <option value="">Select Vehicle Type</option>
                {allVehicleTypes.map((vt) => (
                  <option key={vt} value={vt}>
                    {vt}
                  </option>
                ))}
                <option value="__NEW__">+ Add New Vehicle Type...</option>
              </select>

              {newVehicleForm.isCustomType && (
                <input
                  type="text"
                  placeholder="Enter new vehicle type name..."
                  value={newVehicleForm.vtypeName}
                  onChange={(e) =>
                    setNewVehicleForm((p) => ({ ...p, vtypeName: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              )}
            </div>

            {/* Vehicle Number */}
            <div style={{ marginBottom: "14px" }}>
              <label
                style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "5px" }}
              >
                Vehicle Number *
              </label>
              <input
                type="text"
                placeholder="e.g. MH01AB1234"
                value={newVehicleForm.vehicleNumber}
                onChange={(e) =>
                  setNewVehicleForm((p) => ({ ...p, vehicleNumber: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Ownership */}
            <div style={{ marginBottom: "14px" }}>
              <label
                style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "5px" }}
              >
                Ownership <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: "#f1f5f9",
                  padding: "3px",
                  borderRadius: "8px",
                  border: "1.5px solid #e2e8f0",
                  height: "40px",
                  boxSizing: "border-box",
                  width: "100%",
                }}
              >
                <button
                  type="button"
                  onClick={() => setNewVehicleForm((p) => ({ ...p, self: 1 }))}
                  style={{
                    flex: 1,
                    height: "100%",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: (newVehicleForm.self === 1 || newVehicleForm.self === "1") ? "700" : "500",
                    backgroundColor: (newVehicleForm.self === 1 || newVehicleForm.self === "1") ? "#0f766e" : "transparent",
                    color: (newVehicleForm.self === 1 || newVehicleForm.self === "1") ? "#ffffff" : "#475569",
                    boxShadow: (newVehicleForm.self === 1 || newVehicleForm.self === "1") ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                    transition: "all 0.15s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  <i className="fa-solid fa-car" style={{ fontSize: "12px" }}></i> Self
                </button>
                <button
                  type="button"
                  onClick={() => setNewVehicleForm((p) => ({ ...p, self: 0 }))}
                  style={{
                    flex: 1,
                    height: "100%",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: (newVehicleForm.self === 0 || newVehicleForm.self === "0" || newVehicleForm.self == null) ? "700" : "500",
                    backgroundColor: (newVehicleForm.self === 0 || newVehicleForm.self === "0" || newVehicleForm.self == null) ? "#0f766e" : "transparent",
                    color: (newVehicleForm.self === 0 || newVehicleForm.self === "0" || newVehicleForm.self == null) ? "#ffffff" : "#475569",
                    boxShadow: (newVehicleForm.self === 0 || newVehicleForm.self === "0" || newVehicleForm.self == null) ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                    transition: "all 0.15s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  <i className="fa-solid fa-users" style={{ fontSize: "12px" }}></i> Others
                </button>
              </div>
            </div>

            {/* Owner */}
            <div style={{ marginBottom: "14px" }}>
              <label
                style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "5px" }}
              >
                Owner <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                value={newVehicleForm.ownerID}
                onChange={(e) =>
                  setNewVehicleForm((p) => ({ ...p, ownerID: e.target.value }))
                }
                required
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                  background: "#fff",
                }}
              >
                <option value="">Select Owner (Sundry Creditor) *</option>
                {ownersList.map((o) => (
                  <option key={o.HEAD_CODE} value={o.HEAD_CODE}>
                    {o.OwnerName}
                  </option>
                ))}
              </select>
            </div>

            {/* Model Year */}
            <div style={{ marginBottom: "22px" }}>
              <label
                style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "5px" }}
              >
                Model Year
              </label>
              <input
                type="number"
                min="1990"
                max="2099"
                placeholder="e.g. 2026 (Optional)"
                value={newVehicleForm.modelYear}
                onChange={(e) =>
                  setNewVehicleForm((p) => ({ ...p, modelYear: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                type="button"
                onClick={() => setShowAddVehicleModal(false)}
                style={{
                  padding: "9px 18px",
                  borderRadius: "8px",
                  border: "1.5px solid #cbd5e1",
                  background: "#fff",
                  color: "#475569",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddVehicleSubmit}
                disabled={isAddingVehicle}
                style={{
                  padding: "9px 22px",
                  borderRadius: "8px",
                  border: "none",
                  background: isAddingVehicle ? "#93c5fd" : "#0d6efd",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: isAddingVehicle ? "not-allowed" : "pointer",
                  fontSize: "14px",
                }}
              >
                {isAddingVehicle ? "Saving…" : "Save Vehicle"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ================= PREVIEW MODAL ================= */}
      {showPreviewModal && previewData && (
        <div
          className="dutyslip_modal"
          style={{
            zIndex: 99999,
            backgroundColor: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            className="dutyslip_modal_content"
            style={{
              width: "1120px",
              maxWidth: "96vw",
              height: "92vh",
              background: "#f1f5f9",
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
            }}
          >
            {/* Top Toolbar matching Dashboard Theme */}
            <div
              style={{
                backgroundColor: "#064e48",
                color: "#ffffff",
                padding: "14px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "17px",
                    fontWeight: "700",
                    letterSpacing: "0.3px",
                    color: "#ffffff",
                  }}
                >
                  Duty Slip Preview {previewData.slipId ? `- #${previewData.slipId}` : ""}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowPreviewModal(false)}
                style={{
                  background: "rgba(255, 255, 255, 0.15)",
                  border: "none",
                  borderRadius: "6px",
                  color: "#ffffff",
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "16px",
                  transition: "background 0.2s",
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)")}
                onMouseOut={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)")}
              >
                ✕
              </button>
            </div>

            {/* Action Bar matching Dashboard */}
            <div
              style={{
                padding: "10px 20px",
                backgroundColor: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "12px",
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                onClick={handlePrintPreview}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "6px 14px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#334155",
                  backgroundColor: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Print
              </button>
              <button
                type="button"
                onClick={() => handleDownloadDutyslip(previewData)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "6px 14px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#ffffff",
                  backgroundColor: "#064e48",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Download PDF
              </button>
            </div>

            {/* Scrollable Preview Paper View */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "24px 16px",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
              }}
            >
              <div
                id="duty-slip-preview-render"
                style={{
                  width: "1040px",
                  background: "#ffffff",
                  padding: "24px 32px 36px",
                  borderRadius: "8px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  boxSizing: "border-box",
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  color: "#0f172a",
                }}
              >
                {/* Header Block */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingBottom: "12px",
                    borderBottom: "2px solid #000000",
                    marginBottom: "16px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                    <div
                      style={{
                        width: "52px",
                        height: "52px",
                        background: "#000000",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      {previewCompany?.companyLogoUrl ? (
                        <img
                          src={previewCompany.companyLogoUrl}
                          alt="Logo"
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : null}
                      <span
                        style={{
                          color: "#ffffff",
                          fontWeight: 800,
                          fontSize: "16px",
                          letterSpacing: "1px",
                          display: previewCompany?.companyLogoUrl ? "none" : "inline",
                        }}
                      >
                        {previewCompany?.companyName
                          ? previewCompany.companyName
                              .split(" ")
                              .map((w) => w[0])
                              .join("")
                              .slice(0, 3)
                              .toUpperCase()
                          : "TRY"}
                      </span>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "22px",
                          fontWeight: 900,
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                          color:
                            previewCompany?.companyColor ||
                            previewCompany?.CompanyColourCode ||
                            "#000000",
                          lineHeight: 1.2,
                        }}
                      >
                        {previewCompany?.companyName || "DAZY TOURE TRAVEL'S"}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#64748b",
                          fontWeight: 500,
                          marginTop: "2px",
                        }}
                      >
                        {previewCompany?.companyAddress || "Sahakar Marg"}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "24px",
                      fontWeight: 900,
                      textDecoration: "underline",
                      textUnderlineOffset: "4px",
                      color: "#000000",
                      letterSpacing: "1.5px",
                    }}
                  >
                    DUTY SLIP
                  </div>
                </div>

                {/* Upper Details Grid Card */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.5fr 1fr",
                    gap: "20px",
                    border: "1.5px solid #000000",
                    borderRadius: "12px",
                    padding: "14px 18px",
                    marginBottom: "16px",
                    background: "#ffffff",
                    fontSize: "13px",
                    lineHeight: 1.8,
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", alignItems: "baseline" }}>
                      <span style={{ color: "#64748b", fontWeight: 500 }}>Sr No:</span>
                      <b style={{ color: "#000000", fontWeight: 800 }}>{previewData.slipId}</b>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", alignItems: "baseline" }}>
                      <span style={{ color: "#64748b", fontWeight: 500 }}>Taxi No:</span>
                      <b style={{ color: "#000000", fontWeight: 800 }}>{previewData.taxiDisplay}</b>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", alignItems: "baseline" }}>
                      <span style={{ color: "#64748b", fontWeight: 500 }}>Hired:</span>
                      <b style={{ color: "#000000", fontWeight: 800 }}>{previewData.partyName}</b>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", alignItems: "baseline" }}>
                      <span style={{ color: "#64748b", fontWeight: 500 }}>Client:</span>
                      <b style={{ color: "#000000", fontWeight: 800 }}>{previewData.guestName}</b>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", alignItems: "baseline" }}>
                      <span style={{ color: "#64748b", fontWeight: 500 }}>Address:</span>
                      <b style={{ color: "#000000", fontWeight: 800, wordBreak: "break-word" }}>{previewData.partyAddress}</b>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "85px 1fr", alignItems: "baseline" }}>
                      <span style={{ color: "#64748b", fontWeight: 500 }}>Date:</span>
                      <b style={{ color: "#000000", fontWeight: 800 }}>{formatDisplayDate(previewData.journeyStartDate)}</b>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "85px 1fr", alignItems: "baseline" }}>
                      <span style={{ color: "#64748b", fontWeight: 500 }}>Driver:</span>
                      <b style={{ color: "#000000", fontWeight: 800 }}>{previewData.driverName}</b>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "85px 1fr", alignItems: "baseline" }}>
                      <span style={{ color: "#64748b", fontWeight: 500 }}>Mobile No:</span>
                      <b style={{ color: "#000000", fontWeight: 800 }}>{previewData.hiredMobile}</b>
                    </div>
                  </div>
                </div>

                {/* Table Container */}
                <div
                  style={{
                    border: "1.5px solid #000000",
                    borderRadius: "8px",
                    overflow: "hidden",
                    marginBottom: "24px",
                    background: "#ffffff",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      tableLayout: "fixed",
                      fontSize: "12px",
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#ffffff" }}>
                        <th style={{ border: "1px solid #000", padding: "6px 4px", fontWeight: 700, width: "10%", textAlign: "center" }}>Date</th>
                        <th style={{ border: "1px solid #000", padding: "6px 4px", fontWeight: 700, width: "10%", textAlign: "center" }}>KMStart</th>
                        <th style={{ border: "1px solid #000", padding: "6px 4px", fontWeight: 700, width: "10%", textAlign: "center" }}>KMClose</th>
                        <th style={{ border: "1px solid #000", padding: "6px 4px", fontWeight: 700, width: "10%", textAlign: "center" }}>TotalKM</th>
                        <th style={{ border: "1px solid #000", padding: "6px 4px", fontWeight: 700, width: "9%", textAlign: "center" }}>Opening<br />Time</th>
                        <th style={{ border: "1px solid #000", padding: "6px 4px", fontWeight: 700, width: "9%", textAlign: "center" }}>Closing<br />Time</th>
                        <th style={{ border: "1px solid #000", padding: "6px 4px", fontWeight: 700, width: "10%", textAlign: "center" }}>Total<br />Time</th>
                        <th style={{ border: "1px solid #000", padding: "6px 6px", fontWeight: 700, width: "18%", textAlign: "center" }}>Trip Details</th>
                        <th style={{ border: "1px solid #000", padding: "6px 6px", fontWeight: 700, width: "14%", textAlign: "center" }}>Package</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.tripRows.map((detail, idx) => (
                        <tr key={idx}>
                          <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "center", height: "26px" }}>{formatDisplayDate(detail.date)}</td>
                          <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "center", height: "26px" }}>{detail.kmStartUser || detail.kmStart || detail.kmStartGarage || ""}</td>
                          <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "center", height: "26px" }}>{detail.kmClose || detail.kmCloseGarage || ""}</td>
                          <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "center", height: "26px" }}>{detail.totalKm || ""}</td>
                          <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "center", height: "26px" }}>{detail.openingTime || ""}</td>
                          <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "center", height: "26px" }}>{detail.closingTime || ""}</td>
                          <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "center", height: "26px" }}>{detail.totalTime || ""}</td>
                          <td style={{ border: "1px solid #000", padding: "6px 6px", textAlign: "left", height: "26px" }}>{detail.tripDetails || ""}</td>
                          <td style={{ border: "1px solid #000", padding: "6px 6px", textAlign: "left", height: "26px" }}>{detail.packageName || detail.PackageName || previewData.packageName || ""}</td>
                        </tr>
                      ))}
                      {Array.from({ length: Math.max(0, 5 - previewData.tripRows.length) }).map((_, idx) => (
                        <tr key={`blank-${idx}`}>
                          <td style={{ border: "1px solid #000", height: "26px" }}>&nbsp;</td>
                          <td style={{ border: "1px solid #000", height: "26px" }}>&nbsp;</td>
                          <td style={{ border: "1px solid #000", height: "26px" }}>&nbsp;</td>
                          <td style={{ border: "1px solid #000", height: "26px" }}>&nbsp;</td>
                          <td style={{ border: "1px solid #000", height: "26px" }}>&nbsp;</td>
                          <td style={{ border: "1px solid #000", height: "26px" }}>&nbsp;</td>
                          <td style={{ border: "1px solid #000", height: "26px" }}>&nbsp;</td>
                          <td style={{ border: "1px solid #000", height: "26px" }}>&nbsp;</td>
                          <td style={{ border: "1px solid #000", height: "26px" }}>&nbsp;</td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 700, backgroundColor: "#f1f5f9" }}>
                        <td colSpan={3} style={{ border: "1px solid #000", borderTop: "2px solid #000", textAlign: "right", paddingRight: "16px", fontWeight: 700, fontSize: "12.5px" }}>Grand Total</td>
                        <td style={{ border: "1px solid #000", borderTop: "2px solid #000", textAlign: "center", fontWeight: 700 }}>{previewData.totalKm || ""}</td>
                        <td colSpan={2} style={{ border: "1px solid #000", borderTop: "2px solid #000" }}></td>
                        <td style={{ border: "1px solid #000", borderTop: "2px solid #000", textAlign: "center", fontWeight: 700 }}>{previewData.totalTime || ""}</td>
                        <td colSpan={2} style={{ border: "1px solid #000", borderTop: "2px solid #000" }}></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Signatures & Remarks */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginTop: "24px",
                    padding: "0 4px",
                  }}
                >
                  <div style={{ width: "44%" }}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "14px",
                        textDecoration: "underline",
                        textUnderlineOffset: "3px",
                        color: "#1e293b",
                        marginBottom: "6px",
                      }}
                    >
                      Signature
                    </div>
                    <div style={{ borderBottom: "2px solid #000000", width: "100%", marginTop: "6px" }}></div>
                  </div>
                  <div style={{ width: "44%" }}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "14px",
                        textDecoration: "underline",
                        textUnderlineOffset: "3px",
                        color: "#1e293b",
                        marginBottom: "6px",
                      }}
                    >
                      Remark
                    </div>
                    <div style={{ borderBottom: "2px solid #000000", width: "100%", marginTop: "6px" }}></div>
                    <div style={{ borderBottom: "2px solid #000000", width: "100%", marginTop: "28px" }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dutyslip;
